import { BaseQueryMeta, BaseQueryResult, createApi, FetchArgs } from '@reduxjs/toolkit/query/react';
import type { ThunkDispatch, UnknownAction } from '@reduxjs/toolkit';
import {
  CreateNewTeamRequest,
  ExchangeTokenRequest,
  ItemIdResponse,
  ValidateAuthSessionResponse,
  GetMfeTokenResponse,
  CreateMfeSessionRequest,
  ValidateInviteRequest,
  ValidateInviteResponse,
  AuthenticateTestUserRequest,
  AuthenticateTestUserResponse,
  AuthenticateEmailPasswordUserRequest,
  AuthenticateEmailPasswordUserResponse,
  GenerativeRequestBase,
  GenerativeResponseBase,
  GenerateMcpTokenResponse,
  ExchangeTokenResponse,
  ConnectIntegrationRequestBase,
  ConnectIntegrationResponseBase,
  CompleteConnectIntegrationRequestBase,
  HubSpotStatusResponse,
  StripeStatusResponse,
  SalesforceStatusResponse,
  SalesforceConfigureRequest,
  NetSuiteConfigureRequest,
  NetSuiteStatusResponse,
  QuickBooksStatusResponse,
  CompleteConnectQuickBooksRequest,
  MaxioStatusResponse,
  MaxioConfigureRequest,
  RecurlyStatusResponse,
  RecurlyConfigureRequest,
  MetaStatusResponse,
  MetaAdAccountListResponse,
  UpdateMetaAdAccountSelectionRequest,
  MaxioAdvancedBillingStatusResponse,
  MaxioAdvancedBillingConfigureRequest,
  StayAiStatusResponse,
  StayAiConfigureRequest,
  ShopifyConfigureRequest,
  ShopifyConnectRequest,
  CompleteConnectShopifyRequest,
  ShopifyStatusResponse,
  GoogleAdsStatusResponse,
  GoogleDriveStatusResponse,
  GoogleDrivePickerConfigResponse,
  GetGoogleDriveSelectedFilesResponse,
  SetGoogleDriveSelectedFilesRequest,
  SetGoogleDriveSelectedFolderRequest,
  IntakeAmazonDspStatusResponse,
  SetIntakeAmazonDspFolderRequest,
  TikTokAdsStatusResponse,
  TikTokSellerStatusResponse,
  AmazonAdsStatusResponse,
  AmazonDspStatusResponse,
  ConnectAmazonSellerRequest,
  CompleteConnectAmazonSellerRequest,
  AmazonSellerStatusResponse,
  ConnectGitHubAppResponse,
  CompleteConnectGitHubAppRequest,
  InitiateConnectGitHubAppVerifiedResponse,
  CompleteConnectGitHubAppVerifiedRequest,
  InitiateConnectExistingResponse,
  CompleteConnectExistingRequest,
  DiscoverInstallationsResponse,
  ConnectDiscoveredInstallationRequest,
  GitHubStatusResponse,
  GetRepositoryBranchesResponse,
  SetGitHubRepositoryRequest,
  SetGitHubBranchRequest,
  ProvisionManagedRepoRequest,
  ProvisionManagedRepoResponse,
  LinkGitHubAccountResponse,
  CompleteLinkGitHubAccountRequest,
  ConnectorSchemaRecord,
  ImportResult,
  ImportStatus,
  IngestionRecord,
  QueueAgentOperationResponse,
  PaginatedResult,
  GetDatabricksDataRequest,
  DatabricksSqlQueryResult,
  ExecuteSqlQueryRequest,
  ConnectorSchema,
  BronzeSchemaContextResponse,
  CustomConnectionResponse,
  StartCustomConnectionUploadRequest,
  StartCustomConnectionUploadResponse,
  CompleteCustomConnectionUploadRequest,
  UpdateCustomConnectionRequest,
  IntegrationStatusResponseBase,
  GoldPipelineStatusResponse,
  GoldPipelineStage,
  ConnectorSettingsResponse,
  ConnectorSourceSettings,
  ValidationSuiteDetail,
  ValidationRun,
  ValidationRunSummary,
  SubscriptionDataResponse,
  ContextStatusResponse,
  OpsAppObject,
  OpsAppObjectDefinitionsResponse,
  OpsAppObjectListResponse,
  UpsertOpsAppObjectRequest,
  DeleteOpsAppObjectResponse
} from "./models.ts";
import {
  Team,
  CoreApplicationContext,
  GlobalTeamData,
  TeamRole,
  FirstRunExperienceState,
  Account,
  TeamIdentity,
  QuotaUsage,
  DowngradeViolation,
  CoreContextItem,
  BillingCycleType,
  SelectSourcesAgentAppData,
  DashboardDataModel, DataMappingConfiguration, DashboardDataRecord,
  AgentOperationResultBase, AgentOperationResult, Dashboard, DashboardView, WidgetDataResponse, KpiDefinition, DashboardWidget,
  ConfigurationId, DataPipelineConfiguration, DataPipelineHistoryEntry,
  StartDataExportRequest, DataExportOperationInput,
  UpdateActivePipelineConfigurationIdRequest
} from "@webcore/models/coreModels.ts";
import {
  AccessControlList,
  ComprehensiveDraftDiff,
  ContextFileContentResponse,
  ContextFileListResponse,
  ResolvePermalinkResponse,
  SharedReport,
  SharedReportListItem,
  CreateSharedReportRequest,
  SharingOverviewResponse,
  UpdateViewAccessControlListRequest,
  CreatePermalinkResponse,
  ValidatePermalinkSlugResponse,
  SharedReportResponse
} from "@webcore/models/dashboardModels.ts";
import {
  CoreApplicationContextResponse,
  CreateTeamGroupRequest,
  InviteTeamMemberRequest,
  RemoveTeamInviteRequest,
  UpdateTeamGroupRequest
} from "@webcore/models/teamAccountModels.ts";
import { baseQueryWithResultCheck } from "./BaseQuery.ts";
import { withOperationQueue } from "./RequestQueue.ts";
import Log from "@webcore/services/log/log.ts";
// import { RootState } from '@/store';

const postNoBodyQuery = (url: string, httpVerb?: string): (() => string | FetchArgs) => () => ({
  url: url,
  method: httpVerb ?? "POST"
});

const postQuery = <TArg, TRequest>(url: string, bodyGenerator: (arg: TArg) => TRequest, httpVerb?: string): (((arg: TArg) => string | FetchArgs)) => arg => ({
  url: url,
  method: httpVerb ?? "POST",
  body: bodyGenerator(arg),
  headers: {
    "Content-Type": "application/json"
  }
});

const postQueryDynamicUrl = <TArg, TRequest>(
    urlGenerator: (arg: TArg) => string,
    bodyGenerator: (arg: TArg) => TRequest,
    httpVerb?: string
): ((arg: TArg) => string | FetchArgs) => {
  return arg => postQuery<TArg, TRequest>(
      urlGenerator(arg),
      bodyGenerator,
      httpVerb
  )(arg);
};

const postNoBodyQueryDynamicUrl = <TArg>(
    urlGenerator: (arg: TArg) => string,
    httpVerb?: string
): ((arg: TArg) => string | FetchArgs) => {
  return arg => ({
    url: urlGenerator(arg),
    method: httpVerb ?? "POST"
  });
};

/**
 * Returns the set of cache tags that should be invalidated when the user context changes.
 * This includes team changes, account changes, sign out, and sign in.
 * Centralizing this ensures all team-specific data is properly refreshed.
 */
export const allTagTypes = [
  "CoreApplicationContext",
  "ActiveTeam",
  "AgentData",
  "AgentOperation",
  "ContextItem",
  // Single tag type for every connector's status query. The connector is
  // identified by the `id` on each tag descriptor (the kebab-case service key,
  // e.g. "stripe", "amazon-seller"), so invalidations only flush the affected
  // connector's status cache rather than every connector's.
  "ConnectorStatus",
  // GitHub is intentionally not modeled as a connector in DataFabriq, so its
  // status query uses a dedicated tag rather than ConnectorStatus.
  "GitHubAppStatus",
  "DashboardModel",
  "MappingConfiguration",
  "DashboardRecords",
  "ConnectorSyncData",
  "Dashboards",
  "DataPipelineConfiguration",
  "CustomConnections",
  "WidgetData",
  "ConnectorSettings",
  "MetaAdAccounts",
  "SharedReports",
  "SharingOverview",
  "ViewAccessControlList",
  "GoldPipelineStatus",
  "DataPipelineHistory",
  "ValidationSuite",
  "ValidationRun",
  "ContextFiles",
  "SubscriptionData",
  "ContextStatus",
  // OpsAppObjects stateful store: one tag type for every object type's list query,
  // scoped by the `id` on each tag descriptor (the tableName), so writes and
  // SignalR change notifications only flush the affected object type's cache.
  "OpsAppObjects",
  "OpsAppObjectDefinitions"
] as const;

export type ApiTag = typeof allTagTypes[number];

/**
 * Returns the cache tag descriptor for a given connector's status query. The
 * connector is identified by its kebab-case service key (e.g. "stripe",
 * "amazon-seller"), which is used as the tag `id` so invalidations are
 * scoped to a single connector.
 */
export function connectorStatusTag(serviceKey: string): { type: "ConnectorStatus"; id: string } {
  return { type: "ConnectorStatus", id: serviceKey };
}

// An object type can hold hundreds of thousands of objects, so listOpsAppObjects pages must not
// accumulate in the cache without bound as a custom view pages through them. The cache is capped
// at this many pages; when a newly loaded page pushes past the cap, the oldest unsubscribed pages
// are evicted (pages a mounted hook is displaying are never evicted, so the cache can temporarily
// exceed the cap if more pages than this are on screen at once).
const MAX_CACHED_OPS_APP_OBJECT_LIST_PAGES = 5;

type OpsAppObjectListPageCacheKey = Parameters<typeof api.internalActions.removeQueryResult>[0]["queryCacheKey"];

/** Cache keys of live listOpsAppObjects entries, in insertion order (oldest first). */
const cachedOpsAppObjectListPageKeys = new Set<OpsAppObjectListPageCacheKey>();

/**
 * The selectors returned by dispatching `internal_getRTKQSubscriptions`. RTK Query keeps
 * subscription state inside its middleware (not in the store), and this internal action is its
 * escape hatch for reading it; the return value is untyped, so the shape is declared here.
 */
interface RtkQuerySubscriptionSelectors {
  getSubscriptionCount: (queryCacheKey: string) => number;
}

function evictExcessOpsAppObjectListPages(dispatch: ThunkDispatch<unknown, unknown, UnknownAction>): void {
  if (cachedOpsAppObjectListPageKeys.size <= MAX_CACHED_OPS_APP_OBJECT_LIST_PAGES) {
    return;
  }
  const subscriptionSelectors =
    dispatch(api.internalActions.internal_getRTKQSubscriptions()) as unknown as RtkQuerySubscriptionSelectors;
  for (const queryCacheKey of cachedOpsAppObjectListPageKeys) {
    if (cachedOpsAppObjectListPageKeys.size <= MAX_CACHED_OPS_APP_OBJECT_LIST_PAGES) {
      break;
    }
    // Never evict a page a mounted hook is subscribed to — removing its query result would
    // orphan the hook (RTK Query drops the entry's subscriptions along with the result).
    if (subscriptionSelectors.getSubscriptionCount(queryCacheKey) > 0) {
      continue;
    }
    cachedOpsAppObjectListPageKeys.delete(queryCacheKey);
    dispatch(api.internalActions.removeQueryResult({ queryCacheKey }));
  }
}

export const api= createApi({
  reducerPath: "api",
  baseQuery: baseQueryWithResultCheck,
  tagTypes: allTagTypes,
  endpoints: (builder)=> ({
    validateInvite: builder.query<ValidateInviteResponse, ValidateInviteRequest>({
      query: postQuery(
        "/auth/validate-invite",
        request => request
      ),
      keepUnusedDataFor: 0,
      forceRefetch: () => true,
      extraOptions: {
        isMutationOverride: false
      }
    }),
    validateSignIn: builder.query<ValidateInviteResponse, ValidateInviteRequest>({
      query: postQuery(
          "/auth/validate-invite",
          request => request
      ),
      keepUnusedDataFor: 0,
      forceRefetch: () => true,
      extraOptions: {
        isMutationOverride: false
      }
    }),
    signOut: builder.mutation<void, void>({
      query: postNoBodyQuery("/auth/sign-out"),
      invalidatesTags: _ => allTagTypes,
      extraOptions: {
        isMutationOverride: false
      }
    }),
    authenticateTestUser: builder.mutation<AuthenticateTestUserResponse, AuthenticateTestUserRequest>({
      query: postQuery(
        "/auth/test-authenticate",
        request => ({ ...request })
      ),
      extraOptions: {
        isMutationOverride: false
      }
    }),
    authenticateEmailPasswordUser: builder.mutation<AuthenticateEmailPasswordUserResponse, AuthenticateEmailPasswordUserRequest>({
      query: postQuery(
        "/auth/email-authenticate",
        request => ({ ...request })
      ),
      extraOptions: {
        isMutationOverride: false
      }
    }),
    exchangeIdTokenForDataFabriqToken: builder.mutation<ExchangeTokenResponse, ExchangeTokenRequest>({
      query: postQuery(
        "/auth/exchange-token",
        request => ({ ...request })
      ),
      // invalidate everything, this is a user logging in
      invalidatesTags: _ => allTagTypes,
      async onQueryStarted(request, { dispatch, queryFulfilled }) {
        const result = await queryFulfilled;

        const patchResult = dispatch(
          api.util.updateQueryData("getCoreApplicationContext", undefined, (draft) => {
            Object.assign(draft, result.data.coreApplicationContext);
          })
        );
      },
      extraOptions: {
        isMutationOverride: false
      }
    }),
    validateAuthSession: builder.query<ValidateAuthSessionResponse, { expectedEmail: string; expectedTeamId: string }>({
      query: ({ expectedEmail, expectedTeamId }) => ({
        url: "/auth/validate",
        params: { expectedEmail, expectedTeamId }
      }),
      keepUnusedDataFor: 0,
      forceRefetch: () => true
    }),
    // Exchanges the current full session for a short-lived (20 min) read-only app JWT
    // that the main app hands to an MFE so the MFE can establish its own read-only session.
    getMfeToken: builder.mutation<GetMfeTokenResponse, void>({
      query: postNoBodyQuery("/auth/mfe-token"),
      extraOptions: {
        isMutationOverride: false
      }
    }),
    // Called by the MFE host (on the MFE origin) with the read-only token from the main app.
    // The backend validates it and sets a host-only read-only `datafabriq_cookie_ops` on the
    // MFE origin. Cookie names are per-app (`datafabriq_cookie_app` for the main app) so the
    // two apps' cookies don't collide in local dev where both origins are localhost; the API
    // unions scopes across all valid datafabriq cookies on a request.
    createMfeSession: builder.mutation<void, CreateMfeSessionRequest>({
      query: postQuery(
        "/auth/mfe-session",
        request => ({ ...request })
      ),
      extraOptions: {
        isMutationOverride: false
      }
    }),
    getSelectSourcesAgentData: builder.query<SelectSourcesAgentAppData, { configurationId: ConfigurationId }>({
      query: ({ configurationId }) => `/agent/select-sources/${encodeURIComponent(configurationId)}`,
      providesTags: (result, error, { configurationId }) => [{ type: "AgentData", id: `select-sources-${configurationId}` }]
    }),
    getComprehensiveDraftDiff: builder.query<ComprehensiveDraftDiff, void>({
      query: () => "/teams/active/data-pipeline-configurations/active/diff/draft"
    }),
    updateSelectSourcesAgentData: builder.mutation<void, SelectSourcesAgentAppData>({
      query: postQuery(
        "/agent/select-sources",
        data => data,
        "PUT"
      ),
      invalidatesTags: (result, error, data) => [
        { type: "AgentData", id: `select-sources-${data.applicationContext}` }
      ],
      async onQueryStarted(data, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
            api.util.updateQueryData("getSelectSourcesAgentData", { configurationId: data.applicationContext ?? "" }, (draft) => {
              Object.assign(draft, { ...data });
            })
        );

        try {
          await queryFulfilled;
        }
        catch {
          patchResult.undo();
        }
      }
    }),
    getAgentOperationStatus: builder.query<AgentOperationResultBase | null, { contextId: string; contentId: string; applicationContext?: string | null }>({
      query: ({ contextId, contentId, applicationContext }) => {
        const applicationContextPathComponent = applicationContext ? `/${encodeURIComponent(applicationContext)}` : "";
        return `/agent/${encodeURIComponent(contextId)}/${encodeURIComponent(contentId)}/async${applicationContextPathComponent}`;
      },
      providesTags: (result, error, { contextId, contentId }) => [{ type: "AgentOperation", id: `${contextId}:${contentId}` }],
      keepUnusedDataFor: 0,
      extraOptions: {
        treat404AsNull: true
      }
    }),

    agentGenerate: builder.query<GenerativeResponseBase, { contextId: string; contentId: string; applicationContext?: string | null; inputModel: any }>({
      query: postQueryDynamicUrl(
          request => `/agent/${encodeURIComponent(request.contextId)}/generate`,
          request => ({ contentId: request.contentId, applicationContext: request.applicationContext ?? null, inputModel: request.inputModel }) satisfies GenerativeRequestBase
      ),
      extraOptions: {
        isMutationOverride: false
      }
    }),
    getCoreApplicationContext: builder.query<CoreApplicationContext, void>({
      query: () => "/user/core-application-context",
      providesTags: (coreApplicationContext) => [
        "CoreApplicationContext",
        "ActiveTeam"
      ]
    }),
    updateAppSettings: builder.mutation<CoreApplicationContext, { displayMode: string }>({
      query: postQuery("/user/app-settings", appSettings => appSettings, "PUT"),
      invalidatesTags: _ => ["CoreApplicationContext"],
      async onQueryStarted(appSettings, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          api.util.updateQueryData("getCoreApplicationContext", undefined, (draft) => {
            draft.appSettings = { ...appSettings };
          })
        );
        
        try {
          const result = await queryFulfilled;
          // Update with the returned CoreApplicationContext
          dispatch(
            api.util.updateQueryData("getCoreApplicationContext", undefined, (draft) => {
              Object.assign(draft, result.data);
            })
          );
        } catch {
          patchResult.undo();
        }
      }
    }),
    updateActivePipelineConfigurationId: builder.mutation<CoreApplicationContext, UpdateActivePipelineConfigurationIdRequest>({
      query: postQuery("/user/active-pipeline-configuration-id", request => request, "PUT"),
      invalidatesTags: _ => ["CoreApplicationContext"],
      async onQueryStarted(request, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          api.util.updateQueryData("getCoreApplicationContext", undefined, (draft) => {
            draft.viewingDataPipelineConfigurationId = request.viewingDataPipelineConfigurationId;
          })
        );

        try {
          const result = await queryFulfilled;
          dispatch(
            api.util.updateQueryData("getCoreApplicationContext", undefined, (draft) => {
              Object.assign(draft, result.data);
            })
          );
        } catch {
          patchResult.undo();
        }
      }
    }),
    getTeam: builder.query<Team, void>({
      query: () => "/teams/active",
      providesTags: _ => [{ type: "ActiveTeam" }]
    }),
    updateTeamName: builder.mutation<void, string>({
      query: postQuery("/teams/active/name", newTeamName => ({ newTeamName }), "PUT"),
      invalidatesTags: _ => ["CoreApplicationContext", "ActiveTeam"],
      async onQueryStarted(newTeamName, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
            api.util.updateQueryData("getTeam", undefined, (draft) => {
              draft.teamName = newTeamName;
            })
        );
        const patchResult2 = dispatch(
            api.util.updateQueryData("getCoreApplicationContext", undefined, (draft) => {
              draft.activeTeam = { ...draft.activeTeam, teamName: newTeamName };
            })
        );

        try {
          await queryFulfilled;
        }
        catch {
          patchResult.undo();
          patchResult2.undo();
        }
      }
    }),
    updateFirstRunExperienceState: builder.mutation<void, FirstRunExperienceState>({
      query: postQuery(
          "/teams/active/first-run-experience",
          state => ({ ...state }),
          "PUT"
      ),
      invalidatesTags: _ => ["CoreApplicationContext", "ActiveTeam"],
      async onQueryStarted(newState, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
            api.util.updateQueryData("getTeam", undefined, (draft) => {
              draft.firstRunExperienceState = newState;
            })
        );
        const patchResult2 = dispatch(
            api.util.updateQueryData("getCoreApplicationContext", undefined, (draft) => {
              draft.activeTeam = { ...draft.activeTeam, firstRunExperienceState: newState };
            })
        );

        try {
          await queryFulfilled;
        }
        catch {
          patchResult.undo();
          patchResult2.undo();
        }
      }
    }),
    removeTeamMember: builder.mutation<void, string>({
      query: postNoBodyQueryDynamicUrl(
        memberToRemoveUserId => `/teams/active/members/${encodeURIComponent(memberToRemoveUserId)}`,
        "DELETE"
      ),
      invalidatesTags: _ => ["CoreApplicationContext", "ActiveTeam", "SharingOverview"],
      async onQueryStarted(memberToRemoveUserId, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
            api.util.updateQueryData("getTeam", undefined, (draft) => {
              draft.teamMemberships = draft.teamMemberships.filter(m => m.memberUserId !== memberToRemoveUserId);
            })
        );
        const patchResult2 = dispatch(
            api.util.updateQueryData("getCoreApplicationContext", undefined, (draft) => {
              draft.activeTeam = { ...draft.activeTeam, teamMemberships: draft.activeTeam.teamMemberships.filter(m => m.memberUserId !== memberToRemoveUserId) };
            })
        )

        try {
          await queryFulfilled;
        }
        catch {
          patchResult.undo();
        }
      }
    }),
    updateTeamMemberRole: builder.mutation<void, { memberUserId: string, newRole: TeamRole }>({
      query: postQueryDynamicUrl(
        ({ memberUserId }) => `/teams/active/members/${encodeURIComponent(memberUserId)}/role`,
        ({ newRole }) => ({ newRole }),
        "PUT"
      ),
      invalidatesTags: _ => ["CoreApplicationContext", "ActiveTeam"],
      async onQueryStarted({ memberUserId, newRole }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
            api.util.updateQueryData("getTeam", undefined, (draft) => {
              const memberToUpdate = draft.teamMemberships.find(m => m.memberUserId === memberUserId);
              if (memberToUpdate) {
                memberToUpdate.role = newRole;
              }
            })
        );
        const patchResult2 = dispatch(
            api.util.updateQueryData("getCoreApplicationContext", undefined, (draft) => {
              const memberToUpdate = draft.activeTeam.teamMemberships.find(m => m.memberUserId === memberUserId);
              if (memberToUpdate) {
                memberToUpdate.role = newRole;
              }
            })
        )

        try {
          await queryFulfilled;
        }
        catch {
          patchResult.undo();
          patchResult2.undo();
        }
      }
    }),
    removeTeamInvite: builder.mutation<void, RemoveTeamInviteRequest>({
      query: postQuery("/teams/active/members/invites", request => request, "DELETE"),
      invalidatesTags: _ => ["CoreApplicationContext", "ActiveTeam", "SharingOverview"],
      async onQueryStarted(request, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
            api.util.updateQueryData("getTeam", undefined, (draft) => {
              draft.invitedTeamMembers = draft.invitedTeamMembers.filter(m => m.memberEmailAddress !== request.inviteReference.emailAddress);
            })
        );
        const patchResult2 = dispatch(
            api.util.updateQueryData("getCoreApplicationContext", undefined, (draft) => {
              draft.activeTeam = { ...draft.activeTeam, invitedTeamMembers: draft.activeTeam.invitedTeamMembers.filter(m => m.memberEmailAddress !== request.inviteReference.emailAddress) };
            })
        )

        try {
          await queryFulfilled;
        }
        catch {
          patchResult.undo();
        }
      }
    }),
    inviteTeamMember: builder.mutation<CoreApplicationContextResponse, InviteTeamMemberRequest>({
      query: postQuery("/teams/active/members", invitedTeamMember => ({...invitedTeamMember}), "POST"),
      invalidatesTags: _ => allTagTypes,
      // async onQueryStarted(invitedTeamMember, { dispatch, queryFulfilled }) {
      //   await queryFulfilled;
      //
      //   const patchResult = dispatch(
      //       api.util.updateQueryData("getTeam", undefined, (draft) => {
      //         draft.invitedTeamMembers = [...draft.invitedTeamMembers, { ...invitedTeamMember }];
      //       })
      //   );
      // }
    }),
    createNewTeam: builder.mutation<CoreApplicationContextResponse, CreateNewTeamRequest>({
      query: postQuery("/teams", request => request, "POST"),
      invalidatesTags: _ => allTagTypes,
    }),
    changeActiveTeam: builder.mutation<CoreApplicationContextResponse, TeamIdentity>({
      query: postQuery("/users/active/active-team", activeTeamIdentity => ({ newActiveTeamIdentity: activeTeamIdentity }), "PUT"),
      invalidatesTags: _ => allTagTypes
    }),
    createTeamGroup: builder.mutation<ItemIdResponse, CreateTeamGroupRequest>({
      query: postQuery("/teams/active/groups", data => ({ ...data }), "POST"),
      invalidatesTags: _ => ["CoreApplicationContext", "ActiveTeam"],
    }),
    updateTeamGroup: builder.mutation<void, { groupId: string } & UpdateTeamGroupRequest>({
      query: postQueryDynamicUrl(
        ({ groupId }) => `/teams/active/groups/${encodeURIComponent(groupId)}`,
        ({ name, memberUserIds }) => ({ name, memberUserIds }),
        "PUT"
      ),
      invalidatesTags: _ => ["CoreApplicationContext", "ActiveTeam"],
      async onQueryStarted({ groupId, name, memberUserIds }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          api.util.updateQueryData("getTeam", undefined, (draft) => {
            const group = draft.teamGroups.find(g => g.id === groupId);
            if (group) {
              group.name = name;
              group.memberUserIds = memberUserIds;
            }
          })
        );
        const patchResult2 = dispatch(
          api.util.updateQueryData("getCoreApplicationContext", undefined, (draft) => {
            const group = draft.activeTeam.teamGroups.find(g => g.id === groupId);
            if (group) {
              group.name = name;
              group.memberUserIds = memberUserIds;
            }
          })
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
          patchResult2.undo();
        }
      }
    }),
    deleteTeamGroup: builder.mutation<void, string>({
      query: postNoBodyQueryDynamicUrl(
        groupId => `/teams/active/groups/${encodeURIComponent(groupId)}`,
        "DELETE"
      ),
      invalidatesTags: _ => ["CoreApplicationContext", "ActiveTeam", "Dashboards"],
      async onQueryStarted(groupId, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          api.util.updateQueryData("getTeam", undefined, (draft) => {
            draft.teamGroups = draft.teamGroups.filter(g => g.id !== groupId);
          })
        );
        const patchResult2 = dispatch(
          api.util.updateQueryData("getCoreApplicationContext", undefined, (draft) => {
            draft.activeTeam.teamGroups = draft.activeTeam.teamGroups.filter(g => g.id !== groupId);
          })
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
          patchResult2.undo();
        }
      }
    }),
    getSubscriptionData: builder.query<SubscriptionDataResponse, void>({
      query: () => "/accounts/active/subscription",
      providesTags: _ => ["SubscriptionData"],
      keepUnusedDataFor: 0,
      forceRefetch: () => true
    }),
    updateAccount: builder.mutation<CoreApplicationContextResponse, Account>({
      query: postQuery("/accounts/active", account => ({ ...account }), "PUT"),
      invalidatesTags: _ => allTagTypes
    }),
    createSubscriptionCheckoutSession: builder.mutation<{ clientSecret: string }, { productId: string, priceId: string, userAddOnPriceId: string, userAddOnQuantity: number }>({
      query: postQuery("/accounts/active/checkout", arg => ({ ...arg }), "POST"),
      invalidatesTags: _ => ["SubscriptionData"]
    }),
    updateSubscriptionCheckoutSession: builder.mutation<{ status: string, customerId: string, customerEmail: string, coreApplicationContext: CoreApplicationContext }, { sessionId: string }>({
      query: postQuery("/accounts/active/checkout", request => ({ ...request }), "PUT"),
      invalidatesTags: _ => allTagTypes
    }),
    changeAccountPlan: builder.mutation<CoreApplicationContextResponse, { newPriceId: string }>({
      query: postQuery("/accounts/active/plan", request => ({ ...request }), "PUT"),
      invalidatesTags: _ => allTagTypes
    }),
    changeAccountPlanAddOn: builder.mutation<CoreApplicationContextResponse, { addOnProductId: string, addOnQuantity: number }>({
      query: postQuery("/accounts/active/plan/add-ons", request => ({ ...request }), "PUT"),
      invalidatesTags: _ => allTagTypes
    }),
    cancelAccountPlan: builder.mutation<void, void>({
      query: postNoBodyQuery("/accounts/active/plan", "DELETE"),
      invalidatesTags: _ => allTagTypes
    }),
    getDowngradeViolation: builder.query<{ violation: DowngradeViolation | null }, { productId: string }>({
      query: ({ productId }) => `/accounts/active/plan/downgrade-violation?productId=${encodeURIComponent(productId)}`,
      keepUnusedDataFor: 0,
      forceRefetch: () => true
    }),
    updateGlobalTeamData: builder.mutation<void, GlobalTeamData>({
      query: postQuery("/teams/active/global-team-data", globalTeamData => ({ ...globalTeamData }), "PUT"),
      invalidatesTags: _ => ["CoreApplicationContext", "ActiveTeam" ],
      async onQueryStarted(globalTeamData, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
            api.util.updateQueryData("getTeam", undefined, (draft) => {
              draft.globalTeamData = globalTeamData;
            })
        );
        const patchResult2 = dispatch(
            api.util.updateQueryData("getCoreApplicationContext", undefined, (draft) => {
              draft.activeTeam = { ...draft.activeTeam, globalTeamData };
            })
        )

        try {
          await queryFulfilled;
        }
        catch {
          patchResult.undo();
          patchResult2.undo();
        }
      }
    }),
    getTeamSlug: builder.query<{ slug: string }, void>({
      query: () => "/teams/active/slug",
      providesTags: _ => [{ type: "ActiveTeam" }]
    }),
    updateTeamSlug: builder.mutation<void, string>({
      query: postQuery("/teams/active/slug", newSlug => ({ newSlug }), "PUT"),
      invalidatesTags: _ => ["ActiveTeam"],
      async onQueryStarted(newSlug, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
            api.util.updateQueryData("getTeamSlug", undefined, (draft) => {
              draft.slug = newSlug;
            })
        );

        try {
          await queryFulfilled;
        }
        catch {
          patchResult.undo();
        }
      }
    }),
    validateTeamSlug: builder.query<{ isAvailable: boolean; isValidFormat: boolean }, { slug: string }>({
      query: ({ slug }) => `/teams/validate-slug/${slug}`
    }),
    createMcpServerToken: builder.query<GenerateMcpTokenResponse, string>({
      query: (teamId) => `/mcp/auth/${encodeURIComponent(teamId)}/token`
    }),
    oauth2Consent: builder.mutation<{ redirectUri: string }, {
      clientId: string;
      redirectUri: string;
      scopes: string[];
      state?: string;
      codeChallenge?: string;
      codeChallengeMethod?: string;
      approved: boolean;
    }>({
      query: postQuery(
        "/oauth2/consent",
        request => request
      )
    }),
    getOAuth2Client: builder.query<{
      clientId: string;
      clientName: string;
      clientType: string;
      redirectUris: string[];
      allowedScopes: string[];
    }, string>({
      query: (clientId) => `/oauth2/clients/${encodeURIComponent(clientId)}`
    }),
    // Context Items Management
    getContextItem: builder.query<CoreContextItem, string>({
      query: (contextItemId) => `/teams/active/context-items/${encodeURIComponent(contextItemId)}`,
      providesTags: (_result, _error, contextItemId) => [{ type: "ContextItem", id: contextItemId }]
    }),
    createContextItem: builder.mutation<CoreContextItem, CoreContextItem>({
      query: postQuery("/teams/active/context-items", contextItem => contextItem, "POST")
    }),
    updateContextItem: builder.mutation<CoreContextItem, CoreContextItem>({
      query: postQueryDynamicUrl(
        contextItem => `/teams/active/context-items/${encodeURIComponent(contextItem.id)}`,
        contextItem => contextItem,
        "PUT"
      ),
      invalidatesTags: (_response, _error, contextItem) => [{ type: "ContextItem", id: contextItem.id }]
    }),
    deleteContextItem: builder.mutation<void, string>({
      query: (contextItemId) => ({
        url: `/teams/active/context-items/${encodeURIComponent(contextItemId)}`,
        method: "DELETE"
      }),
      invalidatesTags: (_response, _error, contextItemId) => [{ type: "ContextItem", id: contextItemId }]
    }),
    // ---------- GitHub: app install (regular flow) ----------
    connectGitHubApp: builder.mutation<ConnectGitHubAppResponse, void>({
      query: postNoBodyQuery("/teams/active/github/app/install", "POST"),
      invalidatesTags: ["GitHubAppStatus"]
    }),
    completeConnectGitHubApp: builder.mutation<void, CompleteConnectGitHubAppRequest>({
      query: postQuery("/teams/active/github/app/install", request => request, "PUT"),
      invalidatesTags: ["GitHubAppStatus"]
    }),

    // ---------- GitHub: app install (verified flow — admin-only) ----------
    initiateConnectGitHubAppVerified: builder.mutation<InitiateConnectGitHubAppVerifiedResponse, void>({
      query: postNoBodyQuery("/teams/active/github/app/connect-verified", "POST")
    }),
    completeConnectGitHubAppVerified: builder.mutation<void, CompleteConnectGitHubAppVerifiedRequest>({
      query: postQuery("/teams/active/github/app/connect-verified", request => request, "PUT"),
      invalidatesTags: ["GitHubAppStatus"]
    }),

    // ---------- GitHub: connect-existing flow (admin-only) ----------
    initiateConnectExisting: builder.mutation<InitiateConnectExistingResponse, void>({
      query: postNoBodyQuery("/teams/active/github/app/connect-existing", "POST")
    }),
    completeConnectExisting: builder.mutation<DiscoverInstallationsResponse, CompleteConnectExistingRequest>({
      query: postQuery("/teams/active/github/app/connect-existing", request => request, "PUT")
    }),
    connectDiscoveredInstallation: builder.mutation<void, ConnectDiscoveredInstallationRequest>({
      query: postQuery("/teams/active/github/app/connect-discovered", request => request, "POST"),
      invalidatesTags: ["GitHubAppStatus"]
    }),

    // ---------- GitHub: app disconnection (admin-only) ----------
    disconnectGitHubApp: builder.mutation<void, void>({
      query: postNoBodyQuery("/teams/active/github/app/install", "DELETE"),
      invalidatesTags: ["GitHubAppStatus"]
    }),

    // ---------- GitHub: status / repo / branch ----------
    getGitHubStatus: builder.query<GitHubStatusResponse, void>({
      query: () => "/teams/active/github/status",
      providesTags: ["GitHubAppStatus"],
      // The status includes live branch/repo lists from GitHub — never serve a cached copy.
      keepUnusedDataFor: 0
    }),
    getRepositoryBranches: builder.query<GetRepositoryBranchesResponse, string>({
      query: (repositoryFullName) => `/teams/active/github/repository/branches?repositoryFullName=${encodeURIComponent(repositoryFullName)}`,
      // Branch lists must always reflect GitHub live — never serve a cached copy.
      keepUnusedDataFor: 0
    }),
    setGitHubRepository: builder.mutation<void, SetGitHubRepositoryRequest>({
      query: postQuery("/teams/active/github/repository", request => request, "PUT"),
      invalidatesTags: ["GitHubAppStatus"]
    }),
    setGitHubBranch: builder.mutation<void, SetGitHubBranchRequest>({
      query: postQuery("/teams/active/github/branch", request => request, "PUT"),
      invalidatesTags: ["GitHubAppStatus"]
    }),

    // ---------- GitHub: managed repo provisioning (admin-only) ----------
    provisionManagedRepo: builder.mutation<ProvisionManagedRepoResponse, ProvisionManagedRepoRequest>({
      query: postQuery("/teams/active/github/managed-repo", request => request, "POST"),
      invalidatesTags: ["GitHubAppStatus"]
    }),
    disconnectManagedOrg: builder.mutation<void, void>({
      query: postNoBodyQuery("/teams/active/github/managed-org", "DELETE"),
      invalidatesTags: ["GitHubAppStatus"]
    }),

    // ---------- GitHub: user account linking ----------
    linkGitHubAccount: builder.mutation<LinkGitHubAccountResponse, void>({
      query: postNoBodyQuery("/users/github/link", "POST")
    }),
    completeLinkGitHubAccount: builder.mutation<void, CompleteLinkGitHubAccountRequest>({
      query: postQuery("/users/github/link", request => request, "PUT"),
      invalidatesTags: ["CoreApplicationContext"]
    }),
    unlinkGitHubAccount: builder.mutation<void, void>({
      query: postNoBodyQuery("/users/github/link", "DELETE"),
      invalidatesTags: ["CoreApplicationContext"]
    }),
    // HubSpot Integration
    connectHubSpot: builder.mutation<ConnectIntegrationResponseBase, ConnectIntegrationRequestBase>({
      query: postQuery("/teams/active/connectors/hubspot/install", request => request, "POST"),
      invalidatesTags: [connectorStatusTag("hubspot")]
    }),
    completeConnectHubSpot: builder.mutation<void, CompleteConnectIntegrationRequestBase>({
      query: postQuery("/teams/active/connectors/hubspot/install", request => request, "PUT"),
      invalidatesTags: [connectorStatusTag("hubspot")]
    }),
    disconnectHubSpot: builder.mutation<void, void>({
      query: postNoBodyQuery("/teams/active/connectors/hubspot/install", "DELETE"),
      invalidatesTags: [connectorStatusTag("hubspot")]
    }),
    getHubSpotStatus: builder.query<HubSpotStatusResponse, void>({
      query: () => "/teams/active/connectors/hubspot/status",
      providesTags: [connectorStatusTag("hubspot")]
    }),
    // Stripe Integration
    connectStripe: builder.mutation<ConnectIntegrationResponseBase, ConnectIntegrationRequestBase>({
      query: postQuery("/teams/active/connectors/stripe/install", request => request, "POST"),
      invalidatesTags: [connectorStatusTag("stripe")]
    }),
    completeConnectStripe: builder.mutation<void, CompleteConnectIntegrationRequestBase>({
      query: postQuery("/teams/active/connectors/stripe/install", request => request, "PUT"),
      invalidatesTags: [connectorStatusTag("stripe")]
    }),
    disconnectStripe: builder.mutation<void, void>({
      query: postNoBodyQuery("/teams/active/connectors/stripe/install", "DELETE"),
      invalidatesTags: [connectorStatusTag("stripe")]
    }),
    getStripeStatus: builder.query<StripeStatusResponse, void>({
      query: () => "/teams/active/connectors/stripe/status",
      providesTags: [connectorStatusTag("stripe")]
    }),
    // Salesforce Integration
    configureSalesforce: builder.mutation<SalesforceStatusResponse, SalesforceConfigureRequest>({
      query: postQuery("/teams/active/connectors/salesforce/configure", request => request, "POST"),
      invalidatesTags: [connectorStatusTag("salesforce")]
    }),
    deleteSalesforceCredentials: builder.mutation<void, void>({
      query: postNoBodyQuery("/teams/active/connectors/salesforce/configure", "DELETE"),
      invalidatesTags: [connectorStatusTag("salesforce")]
    }),
    connectSalesforce: builder.mutation<ConnectIntegrationResponseBase, ConnectIntegrationRequestBase>({
      query: postQuery("/teams/active/connectors/salesforce/install", request => request, "POST"),
      invalidatesTags: [connectorStatusTag("salesforce")]
    }),
    completeConnectSalesforce: builder.mutation<void, CompleteConnectIntegrationRequestBase>({
      query: postQuery("/teams/active/connectors/salesforce/install", request => request, "PUT"),
      invalidatesTags: [connectorStatusTag("salesforce")]
    }),
    disconnectSalesforce: builder.mutation<void, void>({
      query: postNoBodyQuery("/teams/active/connectors/salesforce/install", "DELETE"),
      invalidatesTags: [connectorStatusTag("salesforce")]
    }),
    getSalesforceStatus: builder.query<SalesforceStatusResponse, void>({
      query: () => "/teams/active/connectors/salesforce/status",
      providesTags: [connectorStatusTag("salesforce")]
    }),

    // NetSuite Integration (JWT Client Credentials)
    configureNetSuite: builder.mutation<NetSuiteStatusResponse, NetSuiteConfigureRequest>({
      query: postQuery("/teams/active/connectors/netsuite/configure", request => request, "POST"),
      invalidatesTags: [connectorStatusTag("netsuite")]
    }),
    uploadNetSuitePrivateKey: builder.mutation<NetSuiteStatusResponse, string>({
      query: (pemContent) => ({
        url: "/teams/active/connectors/netsuite/private-key",
        method: "POST",
        body: pemContent,
        headers: { "Content-Type": "text/plain" }
      }),
      invalidatesTags: [connectorStatusTag("netsuite")]
    }),
    removeNetSuitePrivateKey: builder.mutation<NetSuiteStatusResponse, void>({
      query: postNoBodyQuery("/teams/active/connectors/netsuite/private-key", "DELETE"),
      invalidatesTags: [connectorStatusTag("netsuite")]
    }),
    connectNetSuite: builder.mutation<ConnectIntegrationResponseBase, ConnectIntegrationRequestBase>({
      query: postQuery("/teams/active/connectors/netsuite/install", request => request, "POST"),
      invalidatesTags: [connectorStatusTag("netsuite")]
    }),
    completeConnectNetSuite: builder.mutation<void, CompleteConnectIntegrationRequestBase>({
      query: postQuery("/teams/active/connectors/netsuite/install", request => request, "PUT"),
      invalidatesTags: [connectorStatusTag("netsuite")]
    }),
    disconnectNetSuite: builder.mutation<void, void>({
      query: postNoBodyQuery("/teams/active/connectors/netsuite/install", "DELETE"),
      invalidatesTags: [connectorStatusTag("netsuite")]
    }),
    getNetSuiteStatus: builder.query<NetSuiteStatusResponse, void>({
      query: () => "/teams/active/connectors/netsuite/status",
      providesTags: [connectorStatusTag("netsuite")]
    }),

    // QuickBooks Integration
    connectQuickBooks: builder.mutation<ConnectIntegrationResponseBase, ConnectIntegrationRequestBase>({
      query: postQuery("/teams/active/connectors/quickbooks/install", request => request, "POST"),
      invalidatesTags: [connectorStatusTag("quickbooks")]
    }),
    completeConnectQuickBooks: builder.mutation<void, CompleteConnectQuickBooksRequest>({
      query: postQuery("/teams/active/connectors/quickbooks/install", request => request, "PUT"),
      invalidatesTags: [connectorStatusTag("quickbooks")]
    }),
    disconnectQuickBooks: builder.mutation<void, void>({
      query: postNoBodyQuery("/teams/active/connectors/quickbooks/install", "DELETE"),
      invalidatesTags: [connectorStatusTag("quickbooks")]
    }),
    getQuickBooksStatus: builder.query<QuickBooksStatusResponse, void>({
      query: () => "/teams/active/connectors/quickbooks/status",
      providesTags: [connectorStatusTag("quickbooks")]
    }),

    // Maxio Integration (Direct Configuration)
    configureMaxio: builder.mutation<MaxioStatusResponse, MaxioConfigureRequest>({
      query: postQuery("/teams/active/connectors/maxio/configure", request => request, "POST"),
      invalidatesTags: [connectorStatusTag("maxio")]
    }),
    disconnectMaxio: builder.mutation<void, void>({
      query: postNoBodyQuery("/teams/active/connectors/maxio/install", "DELETE"),
      invalidatesTags: [connectorStatusTag("maxio")]
    }),
    getMaxioStatus: builder.query<MaxioStatusResponse, void>({
      query: () => "/teams/active/connectors/maxio/status",
      providesTags: [connectorStatusTag("maxio")]
    }),

    // Recurly Integration (Direct Configuration)
    configureRecurly: builder.mutation<RecurlyStatusResponse, RecurlyConfigureRequest>({
      query: postQuery("/teams/active/connectors/recurly/configure", request => request, "POST"),
      invalidatesTags: [connectorStatusTag("recurly")]
    }),
    disconnectRecurly: builder.mutation<void, void>({
      query: postNoBodyQuery("/teams/active/connectors/recurly/install", "DELETE"),
      invalidatesTags: [connectorStatusTag("recurly")]
    }),
    getRecurlyStatus: builder.query<RecurlyStatusResponse, void>({
      query: () => "/teams/active/connectors/recurly/status",
      providesTags: [connectorStatusTag("recurly")]
    }),

    // Meta Integration
    connectMeta: builder.mutation<ConnectIntegrationResponseBase, ConnectIntegrationRequestBase>({
      query: postQuery("/teams/active/connectors/meta/install", request => request, "POST"),
      invalidatesTags: [connectorStatusTag("meta")]
    }),
    completeConnectMeta: builder.mutation<void, CompleteConnectIntegrationRequestBase>({
      query: postQuery("/teams/active/connectors/meta/install", request => request, "PUT"),
      invalidatesTags: [connectorStatusTag("meta"), { type: "MetaAdAccounts" }]
    }),
    disconnectMeta: builder.mutation<void, void>({
      query: postNoBodyQuery("/teams/active/connectors/meta/install", "DELETE"),
      invalidatesTags: [connectorStatusTag("meta")]
    }),
    getMetaStatus: builder.query<MetaStatusResponse, void>({
      query: () => "/teams/active/connectors/meta/status",
      providesTags: [connectorStatusTag("meta")]
    }),
    getMetaAdAccounts: builder.query<MetaAdAccountListResponse, void>({
      query: () => "/teams/active/connectors/meta/ad-accounts",
      providesTags: [{ type: "MetaAdAccounts" }]
    }),
    updateMetaAdAccountSelection: builder.mutation<void, UpdateMetaAdAccountSelectionRequest>({
      query: postQuery("/teams/active/connectors/meta/ad-accounts", request => request, "PUT"),
      invalidatesTags: [connectorStatusTag("meta")]
    }),
    // Maxio Advanced Billing Integration (Direct Configuration)
    configureMaxioAdvancedBilling: builder.mutation<MaxioAdvancedBillingStatusResponse, MaxioAdvancedBillingConfigureRequest>({
      query: postQuery("/teams/active/connectors/maxio-advanced-billing/configure", request => request, "POST"),
      invalidatesTags: [connectorStatusTag("maxio-advanced-billing")]
    }),
    disconnectMaxioAdvancedBilling: builder.mutation<void, void>({
      query: postNoBodyQuery("/teams/active/connectors/maxio-advanced-billing/install", "DELETE"),
      invalidatesTags: [connectorStatusTag("maxio-advanced-billing")]
    }),
    getMaxioAdvancedBillingStatus: builder.query<MaxioAdvancedBillingStatusResponse, void>({
      query: () => "/teams/active/connectors/maxio-advanced-billing/status",
      providesTags: [connectorStatusTag("maxio-advanced-billing")]
    }),

    // Stay.ai Integration (Direct Configuration)
    configureStayAi: builder.mutation<StayAiStatusResponse, StayAiConfigureRequest>({
      query: postQuery("/teams/active/connectors/stay-ai/configure", request => request, "POST"),
      invalidatesTags: [connectorStatusTag("stay-ai")]
    }),
    disconnectStayAi: builder.mutation<void, void>({
      query: postNoBodyQuery("/teams/active/connectors/stay-ai/install", "DELETE"),
      invalidatesTags: [connectorStatusTag("stay-ai")]
    }),
    getStayAiStatus: builder.query<StayAiStatusResponse, void>({
      query: () => "/teams/active/connectors/stay-ai/status",
      providesTags: [connectorStatusTag("stay-ai")]
    }),

    // Google Ads Integration
    connectGoogleAds: builder.mutation<ConnectIntegrationResponseBase, ConnectIntegrationRequestBase>({
      query: postQuery("/teams/active/connectors/google-ads/install", request => request, "POST"),
      invalidatesTags: [connectorStatusTag("google-ads")]
    }),
    completeConnectGoogleAds: builder.mutation<void, CompleteConnectIntegrationRequestBase>({
      query: postQuery("/teams/active/connectors/google-ads/install", request => request, "PUT"),
      invalidatesTags: [connectorStatusTag("google-ads")]
    }),
    disconnectGoogleAds: builder.mutation<void, void>({
      query: postNoBodyQuery("/teams/active/connectors/google-ads/install", "DELETE"),
      invalidatesTags: [connectorStatusTag("google-ads")]
    }),
    getGoogleAdsStatus: builder.query<GoogleAdsStatusResponse, void>({
      query: () => "/teams/active/connectors/google-ads/status",
      providesTags: [connectorStatusTag("google-ads")]
    }),

    // Google Drive Integration
    connectGoogleDrive: builder.mutation<ConnectIntegrationResponseBase, ConnectIntegrationRequestBase>({
      query: postQuery("/teams/active/connectors/google-drive/install", request => request, "POST"),
      invalidatesTags: [connectorStatusTag("google-drive")]
    }),
    completeConnectGoogleDrive: builder.mutation<void, CompleteConnectIntegrationRequestBase>({
      query: postQuery("/teams/active/connectors/google-drive/install", request => request, "PUT"),
      invalidatesTags: [connectorStatusTag("google-drive")]
    }),
    disconnectGoogleDrive: builder.mutation<void, void>({
      query: postNoBodyQuery("/teams/active/connectors/google-drive/install", "DELETE"),
      invalidatesTags: [connectorStatusTag("google-drive")]
    }),
    getGoogleDriveStatus: builder.query<GoogleDriveStatusResponse, void>({
      query: () => "/teams/active/connectors/google-drive/status",
      providesTags: [connectorStatusTag("google-drive")]
    }),
    getGoogleDrivePickerConfig: builder.query<GoogleDrivePickerConfigResponse, void>({
      query: () => "/teams/active/connectors/google-drive/picker-config"
    }),
    getGoogleDriveSelectedFiles: builder.query<GetGoogleDriveSelectedFilesResponse, void>({
      query: () => "/teams/active/connectors/google-drive/selected-files",
      providesTags: [connectorStatusTag("google-drive")]
    }),
    setGoogleDriveSelectedFiles: builder.mutation<void, SetGoogleDriveSelectedFilesRequest>({
      query: postQuery("/teams/active/connectors/google-drive/selected-files", request => request, "PUT"),
      invalidatesTags: [connectorStatusTag("google-drive")]
    }),
    setGoogleDriveSelectedFolder: builder.mutation<void, SetGoogleDriveSelectedFolderRequest>({
      query: postQuery("/teams/active/connectors/google-drive/selected-folder", request => request, "PUT"),
      invalidatesTags: [connectorStatusTag("google-drive")]
    }),

    // Intake Amazon DSP Integration — a second, independent Google Drive OAuth connection,
    // configured with one vendor folder rather than a file selection.
    connectIntakeAmazonDsp: builder.mutation<ConnectIntegrationResponseBase, ConnectIntegrationRequestBase>({
      query: postQuery("/teams/active/connectors/intake-amazon-dsp/install", request => request, "POST"),
      invalidatesTags: [connectorStatusTag("intake-amazon-dsp")]
    }),
    completeConnectIntakeAmazonDsp: builder.mutation<void, CompleteConnectIntegrationRequestBase>({
      query: postQuery("/teams/active/connectors/intake-amazon-dsp/install", request => request, "PUT"),
      invalidatesTags: [connectorStatusTag("intake-amazon-dsp")]
    }),
    disconnectIntakeAmazonDsp: builder.mutation<void, void>({
      query: postNoBodyQuery("/teams/active/connectors/intake-amazon-dsp/install", "DELETE"),
      invalidatesTags: [connectorStatusTag("intake-amazon-dsp")]
    }),
    getIntakeAmazonDspStatus: builder.query<IntakeAmazonDspStatusResponse, void>({
      query: () => "/teams/active/connectors/intake-amazon-dsp/status",
      providesTags: [connectorStatusTag("intake-amazon-dsp")]
    }),
    getIntakeAmazonDspPickerConfig: builder.query<GoogleDrivePickerConfigResponse, void>({
      query: () => "/teams/active/connectors/intake-amazon-dsp/picker-config"
    }),
    setIntakeAmazonDspFolder: builder.mutation<void, SetIntakeAmazonDspFolderRequest>({
      query: postQuery("/teams/active/connectors/intake-amazon-dsp/selected-folder", request => request, "PUT"),
      invalidatesTags: [connectorStatusTag("intake-amazon-dsp")]
    }),

    // TikTok Ads Integration
    connectTikTokAds: builder.mutation<ConnectIntegrationResponseBase, ConnectIntegrationRequestBase>({
      query: postQuery("/teams/active/connectors/tiktok-ads/install", request => request, "POST"),
      invalidatesTags: [connectorStatusTag("tiktok-ads")]
    }),
    completeConnectTikTokAds: builder.mutation<void, CompleteConnectIntegrationRequestBase>({
      query: postQuery("/teams/active/connectors/tiktok-ads/install", request => request, "PUT"),
      invalidatesTags: [connectorStatusTag("tiktok-ads")]
    }),
    disconnectTikTokAds: builder.mutation<void, void>({
      query: postNoBodyQuery("/teams/active/connectors/tiktok-ads/install", "DELETE"),
      invalidatesTags: [connectorStatusTag("tiktok-ads")]
    }),
    getTikTokAdsStatus: builder.query<TikTokAdsStatusResponse, void>({
      query: () => "/teams/active/connectors/tiktok-ads/status",
      providesTags: [connectorStatusTag("tiktok-ads")]
    }),

    // TikTok Seller (TikTok Shop) Integration
    connectTikTokSeller: builder.mutation<ConnectIntegrationResponseBase, ConnectIntegrationRequestBase>({
      query: postQuery("/teams/active/connectors/tiktok-seller/install", request => request, "POST"),
      invalidatesTags: [connectorStatusTag("tiktok-seller")]
    }),
    completeConnectTikTokSeller: builder.mutation<void, CompleteConnectIntegrationRequestBase>({
      query: postQuery("/teams/active/connectors/tiktok-seller/install", request => request, "PUT"),
      invalidatesTags: [connectorStatusTag("tiktok-seller")]
    }),
    disconnectTikTokSeller: builder.mutation<void, void>({
      query: postNoBodyQuery("/teams/active/connectors/tiktok-seller/install", "DELETE"),
      invalidatesTags: [connectorStatusTag("tiktok-seller")]
    }),
    getTikTokSellerStatus: builder.query<TikTokSellerStatusResponse, void>({
      query: () => "/teams/active/connectors/tiktok-seller/status",
      providesTags: [connectorStatusTag("tiktok-seller")]
    }),

    // Shopify Integration
    configureShopify: builder.mutation<ShopifyStatusResponse, ShopifyConfigureRequest>({
      query: postQuery("/teams/active/connectors/shopify/configure", request => request, "POST"),
      invalidatesTags: [connectorStatusTag("shopify")]
    }),
    deleteShopifyCredentials: builder.mutation<void, void>({
      query: postNoBodyQuery("/teams/active/connectors/shopify/configure", "DELETE"),
      invalidatesTags: [connectorStatusTag("shopify")]
    }),
    connectShopify: builder.mutation<ConnectIntegrationResponseBase, ShopifyConnectRequest>({
      query: postQuery("/teams/active/connectors/shopify/install", request => request, "POST"),
      invalidatesTags: [connectorStatusTag("shopify")]
    }),
    completeConnectShopify: builder.mutation<void, CompleteConnectShopifyRequest>({
      query: postQuery("/teams/active/connectors/shopify/install", request => request, "PUT"),
      invalidatesTags: [connectorStatusTag("shopify")]
    }),
    disconnectShopify: builder.mutation<void, void>({
      query: postNoBodyQuery("/teams/active/connectors/shopify/install", "DELETE"),
      invalidatesTags: [connectorStatusTag("shopify")]
    }),
    getShopifyStatus: builder.query<ShopifyStatusResponse, void>({
      query: () => "/teams/active/connectors/shopify/status",
      providesTags: [connectorStatusTag("shopify")]
    }),

    // Amazon Ads Integration (Sponsored Products / Brands / Display)
    connectAmazonAds: builder.mutation<ConnectIntegrationResponseBase, ConnectIntegrationRequestBase>({
      query: postQuery("/teams/active/connectors/amazon-ads/install", request => request, "POST"),
      invalidatesTags: [connectorStatusTag("amazon-ads")]
    }),
    completeConnectAmazonAds: builder.mutation<void, CompleteConnectIntegrationRequestBase>({
      query: postQuery("/teams/active/connectors/amazon-ads/install", request => request, "PUT"),
      invalidatesTags: [connectorStatusTag("amazon-ads")]
    }),
    disconnectAmazonAds: builder.mutation<void, void>({
      query: postNoBodyQuery("/teams/active/connectors/amazon-ads/install", "DELETE"),
      invalidatesTags: [connectorStatusTag("amazon-ads")]
    }),
    getAmazonAdsStatus: builder.query<AmazonAdsStatusResponse, void>({
      query: () => "/teams/active/connectors/amazon-ads/status",
      providesTags: [connectorStatusTag("amazon-ads")]
    }),

    // Amazon DSP Integration (Demand-Side Platform display advertising)
    connectAmazonDsp: builder.mutation<ConnectIntegrationResponseBase, ConnectIntegrationRequestBase>({
      query: postQuery("/teams/active/connectors/amazon-dsp/install", request => request, "POST"),
      invalidatesTags: [connectorStatusTag("amazon-dsp")]
    }),
    completeConnectAmazonDsp: builder.mutation<void, CompleteConnectIntegrationRequestBase>({
      query: postQuery("/teams/active/connectors/amazon-dsp/install", request => request, "PUT"),
      invalidatesTags: [connectorStatusTag("amazon-dsp")]
    }),
    disconnectAmazonDsp: builder.mutation<void, void>({
      query: postNoBodyQuery("/teams/active/connectors/amazon-dsp/install", "DELETE"),
      invalidatesTags: [connectorStatusTag("amazon-dsp")]
    }),
    getAmazonDspStatus: builder.query<AmazonDspStatusResponse, void>({
      query: () => "/teams/active/connectors/amazon-dsp/status",
      providesTags: [connectorStatusTag("amazon-dsp")]
    }),

    // Amazon Seller Integration (Selling Partner API)
    connectAmazonSeller: builder.mutation<ConnectIntegrationResponseBase, ConnectAmazonSellerRequest>({
      query: postQuery("/teams/active/connectors/amazon-seller/install", request => request, "POST"),
      invalidatesTags: [connectorStatusTag("amazon-seller")]
    }),
    completeConnectAmazonSeller: builder.mutation<void, CompleteConnectAmazonSellerRequest>({
      query: postQuery("/teams/active/connectors/amazon-seller/install", request => request, "PUT"),
      invalidatesTags: [connectorStatusTag("amazon-seller")]
    }),
    disconnectAmazonSeller: builder.mutation<void, void>({
      query: postNoBodyQuery("/teams/active/connectors/amazon-seller/install", "DELETE"),
      invalidatesTags: [connectorStatusTag("amazon-seller")]
    }),
    getAmazonSellerStatus: builder.query<AmazonSellerStatusResponse, void>({
      query: () => "/teams/active/connectors/amazon-seller/status",
      providesTags: [connectorStatusTag("amazon-seller")]
    }),

    // Connector Sync Endpoints
    //
    // The serviceKey argument is the connector's wire identifier: the kebab-case service key for
    // standard connectors (the ConnectorType enum value), or a custom connection's name. For a
    // source obtained from an ImportResult, pass its server-provided serviceKey field.
    getConnectorSchemas: builder.query<ConnectorSchema[], { serviceKey: string }>({
      query: arg => `/teams/active/connectors/${encodeURIComponent(arg.serviceKey)}/schemas`,
      providesTags: [{ type: "ConnectorSyncData" }]
    }),
    getConnectorSyncStatus: builder.query<ImportResult, { serviceKey: string }>({
      query: arg => `/teams/active/connectors/${encodeURIComponent(arg.serviceKey)}/sync/status`,
      providesTags: [{ type: "ConnectorSyncData" }]
    }),
    getConnectorSyncHistory: builder.query<ImportResult[], { serviceKey: string }>({
      query: arg => `/teams/active/connectors/${encodeURIComponent(arg.serviceKey)}/sync/history`,
      providesTags: [{ type: "ConnectorSyncData" }]
    }),
    getConnectorRecords: builder.query<PaginatedResult<IngestionRecord>, { serviceKey: string; recordType: string; skip?: number; take?: number }>({
      query: arg => {
        const params = new URLSearchParams();
        params.append('recordType', arg.recordType);
        if (arg.skip !== undefined) params.append('skip', arg.skip.toString());
        if (arg.take !== undefined) params.append('take', arg.take.toString());
        const queryString = params.toString();
        return `/teams/active/connectors/${encodeURIComponent(arg.serviceKey)}/records${queryString ? `?${queryString}` : ''}`;
      },
      providesTags: [{ type: "ConnectorSyncData" }]
    }),
    getAllConnectorSyncStatuses: builder.query<ImportResult[], void>({
      query: () => "/teams/active/connectors/sync/status",
      providesTags: [{ type: "ConnectorSyncData" }]
    }),
    getBronzeSchemaContext: builder.query<BronzeSchemaContextResponse, void>({
      query: () => "/teams/active/bronze-schema-context",
      providesTags: [{ type: "ConnectorSyncData" }]
    }),
    getGoldPipelineStatus: builder.query<GoldPipelineStatusResponse | null, { dataPipelineConfigurationId: string }>({
      query: ({ dataPipelineConfigurationId }) => `/teams/active/gold-pipeline/${encodeURIComponent(dataPipelineConfigurationId)}/status`,
      providesTags: [{ type: "GoldPipelineStatus" }],
      extraOptions: {
        treat404AsNull: true
      }
    }),
    triggerConnectorSync: builder.mutation<void, { serviceKey: string }>({
      query: postNoBodyQueryDynamicUrl(
        arg => `/teams/active/connectors/${encodeURIComponent(arg.serviceKey)}/sync`
      ),
      invalidatesTags: (_result, _error, arg) => [{ type: "ConnectorSyncData" }, connectorStatusTag(arg.serviceKey)],
      // Optimistically flip the clicked source to Queued so the status appears instantly on
      // click, rather than waiting on the POST plus the invalidation-driven refetch. The real
      // server state (and later "Syncing" via the SignalR change feed) replaces it when it lands.
      async onQueryStarted({ serviceKey }, { dispatch, queryFulfilled }) {
        const nowIso = new Date().toISOString();
        const patchResult = dispatch(
          api.util.updateQueryData("getAllConnectorSyncStatuses", undefined, draft => {
            const source = draft.find(s => s.serviceKey === serviceKey);
            if (source) {
              source.status = ImportStatus.Queued;
              source.lastUpdated = nowIso;
              // A queued import has not started yet — the backend only sets startedAt when the
              // worker calls StartImportAsync — so leave it null to match the real server state.
              source.startedAt = null;
              source.completedAt = null;
              source.errorMessage = null;
              source.recordsFetched = 0;
              source.recordsStored = 0;
              source.progress = 0;
              source.tableSummaries = null;
            }
          })
        );

        try {
          await queryFulfilled;
        }
        catch {
          patchResult.undo();
        }
      }
    }),
    getConnectorSettings: builder.query<ConnectorSettingsResponse, { service: string }>({
      query: ({ service }) => `/teams/active/connectors/${service}/settings`,
      providesTags: (result, error, { service }) => [{ type: "ConnectorSettings" as const, id: service }]
    }),
    updateConnectorSettings: builder.mutation<void, { service: string; settings: ConnectorSourceSettings }>({
      query: ({ service, settings }) => ({
        url: `/teams/active/connectors/${service}/settings`,
        method: 'PUT',
        body: { settings },
        headers: { "Content-Type": "application/json" }
      }),
      // Record-type edits can change derived state surfaced by the connector status
      // (e.g. HubSpot's requiresReauthorization flag), so invalidate both caches.
      invalidatesTags: (result, error, { service }) => [
        { type: "ConnectorSettings" as const, id: service },
        connectorStatusTag(service)
      ]
    }),

    // Connector Sync Schedule — single set of endpoints for every connector;
    // the connector is identified by the kebab-case `serviceKey` path segment.
    updateConnectorSyncSchedule: builder.mutation<void, { serviceKey: string; cronExpression: string | null }>({
      query: postQueryDynamicUrl(
        arg => `/teams/active/connectors/${encodeURIComponent(arg.serviceKey)}/sync-schedule`,
        arg => ({ cronExpression: arg.cronExpression }),
        "PUT"
      ),
      invalidatesTags: (result, error, arg) => [connectorStatusTag(arg.serviceKey)]
    }),
    startConnectorSyncSchedule: builder.mutation<void, { serviceKey: string; cronExpression: string }>({
      query: postQueryDynamicUrl(
        arg => `/teams/active/connectors/${encodeURIComponent(arg.serviceKey)}/sync-schedule/start`,
        arg => ({ cronExpression: arg.cronExpression }),
        "POST"
      ),
      invalidatesTags: (result, error, arg) => [connectorStatusTag(arg.serviceKey)]
    }),
    stopConnectorSyncSchedule: builder.mutation<void, { serviceKey: string }>({
      query: postNoBodyQueryDynamicUrl(
        arg => `/teams/active/connectors/${encodeURIComponent(arg.serviceKey)}/sync-schedule/stop`,
        "POST"
      ),
      invalidatesTags: (result, error, arg) => [connectorStatusTag(arg.serviceKey)]
    }),

    // Custom Connections
    getCustomStatus: builder.query<IntegrationStatusResponseBase, void>({
      query: () => "/teams/active/connectors/custom/status",
      providesTags: [connectorStatusTag("custom")]
    }),
    getCustomConnections: builder.query<CustomConnectionResponse[], void>({
      query: () => "/teams/active/custom-connections",
      providesTags: [{ type: "CustomConnections" }]
    }),
    getCustomConnection: builder.query<CustomConnectionResponse, { connectionId: string }>({
      query: ({ connectionId }) => `/teams/active/custom-connections/${connectionId}`,
      providesTags: (result, error, { connectionId }) => [{ type: "CustomConnections", id: connectionId }]
    }),
    startCustomConnectionUpload: builder.mutation<StartCustomConnectionUploadResponse, StartCustomConnectionUploadRequest>({
      // Nothing is persisted by this call (deferred creation), so nothing to invalidate.
      query: postQuery("/teams/active/custom-connections/uploads", request => request, "POST")
    }),
    completeCustomConnectionUpload: builder.mutation<CustomConnectionResponse, { uploadId: string; body: CompleteCustomConnectionUploadRequest }>({
      query: postQueryDynamicUrl(
        arg => `/teams/active/custom-connections/uploads/${arg.uploadId}/complete`,
        arg => arg.body,
        "POST"
      ),
      invalidatesTags: (result, error, arg) => [
        { type: "CustomConnections" },
        { type: "CustomConnections", id: arg.body.connectionId },
        connectorStatusTag("custom")
      ]
    }),
    updateCustomConnection: builder.mutation<CustomConnectionResponse, { connectionId: string; body: UpdateCustomConnectionRequest }>({
      query: postQueryDynamicUrl(
        arg => `/teams/active/custom-connections/${arg.connectionId}`,
        arg => arg.body,
        "PUT"
      ),
      invalidatesTags: (result, error, { connectionId }) => [{ type: "CustomConnections" }, { type: "CustomConnections", id: connectionId }]
    }),
    deleteCustomConnection: builder.mutation<void, { connectionId: string }>({
      query: postNoBodyQueryDynamicUrl(
        arg => `/teams/active/custom-connections/${arg.connectionId}`,
        "DELETE"
      ),
      invalidatesTags: [{ type: "CustomConnections" }, { type: "ConnectorSyncData" }, connectorStatusTag("custom")]
    }),

    // Dashboard Data Model and Data Mapping Configuration Endpoints. Both are scoped by pipeline
    // configuration id, not by the object's own id: where the objects live depends on the configuration
    // (assembled from the team's repo at Version>=3, read from Cosmos below that), so only the backend's
    // configuration-scoped read resolves the right storage.
    getDashboardModel: builder.query<DashboardDataModel, { dataPipelineConfigurationId: string }>({
      query: ({ dataPipelineConfigurationId }) =>
        `/teams/active/dashboard-model/${encodeURIComponent(dataPipelineConfigurationId)}`,
      providesTags: (_result, _error, { dataPipelineConfigurationId }) =>
        [{ type: "DashboardModel", id: dataPipelineConfigurationId }]
    }),
    getMappingConfiguration: builder.query<DataMappingConfiguration, { dataPipelineConfigurationId: string }>({
      query: ({ dataPipelineConfigurationId }) =>
        `/teams/active/data-mapping/${encodeURIComponent(dataPipelineConfigurationId)}`,
      providesTags: (_result, _error, { dataPipelineConfigurationId }) =>
        [{ type: "MappingConfiguration", id: dataPipelineConfigurationId }]
    }),
    // Dashboard Data Records Endpoints
    getDashboardRecords: builder.query<PaginatedResult<DashboardDataRecord>, { objectType: string; skip: number; take: number; dataPipelineConfigurationId: string }>({
      query: (params) => {
        const queryParams = new URLSearchParams();
        queryParams.append('objectType', params.objectType);
        queryParams.append('skip', params.skip.toString());
        queryParams.append('take', params.take.toString());
        const queryString = queryParams.toString();
        return `/teams/active/dashboard-data/${encodeURIComponent(params.dataPipelineConfigurationId)}${queryString ? `?${queryString}` : ''}`;
      },
      providesTags:  (_result, _error, request) =>  [{ type: "DashboardRecords", id: `${request.dataPipelineConfigurationId}:${request.objectType}:${request.skip}:${request.take}` }]
    }),

    // Databricks Data Query Endpoint (dynamic team-based)
    getDatabricksData: builder.query<DatabricksSqlQueryResult, GetDatabricksDataRequest>({
      query: postQuery(
        "/databricks/data",
        request => request,
        "POST"
      )
    }),

    // Ad-hoc SQL Query Endpoint (bronze/gold schemas)
    executeSqlQuery: builder.mutation<DatabricksSqlQueryResult, ExecuteSqlQueryRequest>({
      query: postQuery(
        "/databricks/query",
        request => request,
        "POST"
      )
    }),

    // KPI Dashboards Endpoints - use ID-based lookups
    getDashboard: builder.query<Dashboard, { id: string }>({
      query: ({ id }) => `/dashboards/${id}`,
      providesTags: (result, error, { id }) => [{ type: "Dashboards", id }]
    }),
    getWidgetData: builder.query<WidgetDataResponse, { dashboardId: string; viewId: string; widgetId: string; filters?: Record<string, string>; dataPipelineConfigurationId: string }>({
      queryFn: async ({ dashboardId, viewId, widgetId, filters, dataPipelineConfigurationId }, _api, _extraOptions, baseQuery) => {
        const params = new URLSearchParams(filters ?? {});
        const queryString = params.toString();
        const url = `/teams/active/dashboards/${dashboardId}/views/${viewId}/widgets/${widgetId}/data/${encodeURIComponent(dataPipelineConfigurationId)}${queryString ? `?${queryString}` : ''}`;

        const result = await withOperationQueue("widgetData", async () => baseQuery(url), 3);

        return result.error
          ? { error: result.error }
          : { data: result.data as WidgetDataResponse };
      },
      providesTags: (_result, _error, { widgetId, dataPipelineConfigurationId }) => [{ type: "WidgetData", id: `${dataPipelineConfigurationId}:${widgetId}` }]
    }),
    getDrilldownData: builder.query<WidgetDataResponse, { dashboardId: string; viewId: string; widgetId: string; filters?: Record<string, string> }>({
      query: postQueryDynamicUrl(
        ({ dashboardId, viewId, widgetId }) => `/teams/active/dashboards/${dashboardId}/views/${viewId}/widgets/${widgetId}/drilldown`,
        ({ filters }) => ({ filters: filters ?? {} })
      ),
      extraOptions: {
        isMutationOverride: false
      }
    }),
    // Custom Dashboard View Endpoints
    getKpiData: builder.query<WidgetDataResponse, { dashboardId: string; kpiDefinitionId: string; filters?: Record<string, string>; dataPipelineConfigurationId: string }>({
      query: ({ dashboardId, kpiDefinitionId, filters, dataPipelineConfigurationId }) => {
        const params = new URLSearchParams(filters ?? {});
        const queryString = params.toString();
        return `/teams/active/dashboards/${dashboardId}/kpis/${kpiDefinitionId}/data/${encodeURIComponent(dataPipelineConfigurationId)}${queryString ? `?${queryString}` : ''}`;
      },
      providesTags: (_result, _error, { kpiDefinitionId, dataPipelineConfigurationId }) => [{ type: "WidgetData", id: `${dataPipelineConfigurationId}:kpi:${kpiDefinitionId}` }]
    }),

    // OpsAppObjects stateful store — team-scoped objects read/written by custom
    // dashboard view/widget code. List queries are tagged per tableName so writes
    // and SignalR change notifications only refresh the affected object type.
    getOpsAppObjectDefinitions: builder.query<OpsAppObjectDefinitionsResponse, { dataPipelineConfigurationId: string }>({
      query: ({ dataPipelineConfigurationId }) =>
        `/teams/active/ops-app-objects/definitions/${encodeURIComponent(dataPipelineConfigurationId)}`,
      // Definitions change with pipeline config publishes.
      providesTags: ["OpsAppObjectDefinitions"]
    }),
    listOpsAppObjects: builder.query<OpsAppObjectListResponse, { dataPipelineConfigurationId: string; tableName: string; skip: number; take: number }>({
      // Paging is mandatory — an object type can hold a large data set, so callers always page explicitly.
      query: ({ dataPipelineConfigurationId, tableName, skip, take }) =>
        `/teams/active/ops-app-objects/${encodeURIComponent(dataPipelineConfigurationId)}/${encodeURIComponent(tableName)}?skip=${skip}&take=${take}`,
      providesTags: (_result, _error, { tableName }) => [{ type: "OpsAppObjects", id: tableName }],
      // Bounds the page cache to MAX_CACHED_OPS_APP_OBJECT_LIST_PAGES: registers this page in the
      // insertion-ordered key set once its data (and thus its tag registration) has landed, evicts
      // the oldest unsubscribed pages beyond the cap, and drops the registration when the entry
      // leaves the cache (whether evicted here or expired by RTK Query itself).
      async onCacheEntryAdded(arg, { getState, dispatch, cacheDataLoaded, cacheEntryRemoved }) {
        try {
          await cacheDataLoaded;
        } catch {
          // The entry errored or was removed before any data landed — there is no page to track.
          return;
        }
        const entry = api.util
          .selectInvalidatedBy(getState(), [{ type: "OpsAppObjects", id: arg.tableName }])
          .find(candidate => candidate.endpointName === "listOpsAppObjects"
            && candidate.originalArgs.dataPipelineConfigurationId === arg.dataPipelineConfigurationId
            && candidate.originalArgs.tableName === arg.tableName
            && candidate.originalArgs.skip === arg.skip
            && candidate.originalArgs.take === arg.take);
        if (entry === undefined) {
          return;
        }
        const queryCacheKey = entry.queryCacheKey as OpsAppObjectListPageCacheKey;
        cachedOpsAppObjectListPageKeys.delete(queryCacheKey);
        cachedOpsAppObjectListPageKeys.add(queryCacheKey);
        evictExcessOpsAppObjectListPages(dispatch);
        await cacheEntryRemoved;
        cachedOpsAppObjectListPageKeys.delete(queryCacheKey);
      }
    }),
    getOpsAppObject: builder.query<OpsAppObject, { dataPipelineConfigurationId: string; tableName: string; objectId: string }>({
      query: ({ dataPipelineConfigurationId, tableName, objectId }) =>
        `/teams/active/ops-app-objects/${encodeURIComponent(dataPipelineConfigurationId)}/${encodeURIComponent(tableName)}/objects/${encodeURIComponent(objectId)}`,
      providesTags: (_result, _error, { tableName }) => [{ type: "OpsAppObjects", id: tableName }]
    }),
    upsertOpsAppObject: builder.mutation<OpsAppObject, { dataPipelineConfigurationId: string; tableName: string; objectId: string } & UpsertOpsAppObjectRequest>({
      query: postQueryDynamicUrl(
        ({ dataPipelineConfigurationId, tableName, objectId }) => `/teams/active/ops-app-objects/${encodeURIComponent(dataPipelineConfigurationId)}/${encodeURIComponent(tableName)}/objects/${encodeURIComponent(objectId)}`,
        ({ properties }) => ({ properties }),
        "PUT"
      ),
      invalidatesTags: (_result, _error, { tableName }) => [{ type: "OpsAppObjects", id: tableName }],
      // Optimistically patch the cached pages and single-object entries that already hold this
      // object so custom view code sees the write immediately. New objects are NOT inserted
      // optimistically — their sequence position isn't known client-side, so they can't be mapped
      // to a page; the tag invalidation refetch surfaces them with authoritative ordering.
      async onQueryStarted({ dataPipelineConfigurationId, tableName, objectId, properties }, { dispatch, getState, queryFulfilled }) {
        const listArgs = api.util
          .selectCachedArgsForQuery(getState(), "listOpsAppObjects")
          .filter(args => args.dataPipelineConfigurationId === dataPipelineConfigurationId && args.tableName === tableName);
        const getArgs = api.util
          .selectCachedArgsForQuery(getState(), "getOpsAppObject")
          .filter(args => args.dataPipelineConfigurationId === dataPipelineConfigurationId && args.tableName === tableName && args.objectId === objectId);

        const undos: (() => void)[] = [];

        // Replace the object's properties in place on whichever cached page holds it.
        for (const args of listArgs) {
          undos.push(dispatch(
            api.util.updateQueryData("listOpsAppObjects", args, (draft) => {
              const item = draft.items.find(candidate => candidate.id === objectId);
              if (item) {
                item.properties = properties;
              }
            })
          ).undo);
        }

        for (const args of getArgs) {
          undos.push(dispatch(
            api.util.updateQueryData("getOpsAppObject", args, (draft) => {
              draft.properties = properties;
            })
          ).undo);
        }

        try {
          const result = await queryFulfilled;
          // Overwrite the optimistic placeholders with the authoritative item so caches are
          // correct even before the invalidation refetch lands.
          for (const args of listArgs) {
            dispatch(
              api.util.updateQueryData("listOpsAppObjects", args, (draft) => {
                const index = draft.items.findIndex(candidate => candidate.id === objectId);
                if (index >= 0) {
                  draft.items[index] = result.data;
                }
              })
            );
          }
          for (const args of getArgs) {
            dispatch(
              api.util.updateQueryData("getOpsAppObject", args, (draft) => {
                Object.assign(draft, result.data);
              })
            );
          }
        } catch {
          undos.forEach(undo => undo());
        }
      }
    }),
    deleteOpsAppObject: builder.mutation<DeleteOpsAppObjectResponse, { dataPipelineConfigurationId: string; tableName: string; objectId: string }>({
      query: postNoBodyQueryDynamicUrl(
        ({ dataPipelineConfigurationId, tableName, objectId }) => `/teams/active/ops-app-objects/${encodeURIComponent(dataPipelineConfigurationId)}/${encodeURIComponent(tableName)}/objects/${encodeURIComponent(objectId)}`,
        "DELETE"
      ),
      invalidatesTags: (_result, _error, { tableName }) => [{ type: "OpsAppObjects", id: tableName }],
      // Optimistically drop the object from every cached page; the tag invalidation refetch then
      // reconciles paging boundaries and totalCount authoritatively.
      async onQueryStarted({ dataPipelineConfigurationId, tableName, objectId }, { dispatch, getState, queryFulfilled }) {
        const listArgs = api.util
          .selectCachedArgsForQuery(getState(), "listOpsAppObjects")
          .filter(args => args.dataPipelineConfigurationId === dataPipelineConfigurationId && args.tableName === tableName);

        const undos: (() => void)[] = [];

        let foundInCache = false;
        for (const args of listArgs) {
          undos.push(dispatch(
            api.util.updateQueryData("listOpsAppObjects", args, (draft) => {
              const index = draft.items.findIndex(candidate => candidate.id === objectId);
              if (index >= 0) {
                draft.items.splice(index, 1);
                foundInCache = true;
              }
            })
          ).undo);
        }

        if (foundInCache) {
          for (const args of listArgs) {
            undos.push(dispatch(
              api.util.updateQueryData("listOpsAppObjects", args, (draft) => {
                draft.totalCount -= 1;
              })
            ).undo);
          }
        }

        try {
          await queryFulfilled;
        } catch {
          undos.forEach(undo => undo());
        }
      }
    }),

    // Dedicated ACL read path for a live view — the dashboard contract no longer carries the ACL.
    getDashboardViewAccessControlList: builder.query<AccessControlList, { dashboardId: string; viewId: string }>({
      query: ({ dashboardId, viewId }) => `/dashboards/${encodeURIComponent(dashboardId)}/views/${encodeURIComponent(viewId)}/access-control-list`,
      providesTags: (result, error, { viewId }) => [{ type: "ViewAccessControlList", id: viewId }]
    }),

    updateDashboardViewAccessControlList: builder.mutation<Dashboard, { dashboardId: string; viewId: string } & UpdateViewAccessControlListRequest>({
      query: postQueryDynamicUrl(
        ({ dashboardId, viewId }) => `/dashboards/${encodeURIComponent(dashboardId)}/views/${encodeURIComponent(viewId)}/access-control-list`,
        ({ accessControlList }) => ({ accessControlList }),
        "PUT"
      ),
      invalidatesTags: (result, error, { viewId }) => ["Dashboards", "SharingOverview", { type: "ViewAccessControlList", id: viewId }]
    }),

    createViewPermalink: builder.mutation<CreatePermalinkResponse, { dashboardId: string; viewId: string; dataPipelineConfigurationId: string; slug: string }>({
      query: postQueryDynamicUrl(
        ({ dashboardId, viewId, dataPipelineConfigurationId }) => `/teams/active/dashboards/${encodeURIComponent(dashboardId)}/views/${encodeURIComponent(viewId)}/${encodeURIComponent(dataPipelineConfigurationId)}/permalink`,
        ({ slug }) => ({ slug }),
        "POST"
      ),
      invalidatesTags: ["Dashboards", "SharingOverview"]
    }),

    validatePermalinkSlug: builder.query<ValidatePermalinkSlugResponse, { slug: string; currentSlug?: string }>({
      query: ({ slug, currentSlug }) => {
        const base = `/teams/active/permalink-slugs/validate/${encodeURIComponent(slug)}`;
        return currentSlug ? `${base}?currentSlug=${encodeURIComponent(currentSlug)}` : base;
      }
    }),

    // resolvePermalink and getSharedReport are opened from external links (a URL the user clicked)
    // that may belong to a team other than the active one, so they resolve the team slug from the URL
    // and switch the caller's active team. They are modeled as RTK mutations even though the HTTP
    // request is a GET — that lets them invalidate all tags like changeActiveTeam and refresh every
    // cached query for the (possibly) new team, and their CoreApplicationContext-bearing response
    // updates the store via the generic context matcher in userContextSlice.
    // extraOptions.isMutationOverride=false keeps the top-of-app "saving data" spinner from showing
    // for these read-shaped operations.
    //
    // Every other sharing endpoint below is invoked from in-app UI where the user is already on a
    // team, so it targets /teams/active, never switches teams, and returns a plain payload.
    resolvePermalink: builder.mutation<ResolvePermalinkResponse, { teamSlug: string; slug: string; type: string }>({
      query: ({ teamSlug, slug, type }) => `/teams/${encodeURIComponent(teamSlug)}/permalinks/${encodeURIComponent(type)}/${encodeURIComponent(slug)}`,
      invalidatesTags: _ => allTagTypes,
      extraOptions: {
        isMutationOverride: false
      }
    }),

    // Shared Reports
    createSharedReport: builder.mutation<SharedReport, CreateSharedReportRequest>({
      query: postQuery("/teams/active/shared-reports", request => request),
      invalidatesTags: ["SharedReports", "SharingOverview"]
    }),

    listSharedReports: builder.query<SharedReportListItem[], void>({
      query: () => `/teams/active/shared-reports`,
      providesTags: ["SharedReports"]
    }),

    getSharedReport: builder.mutation<SharedReportResponse, { teamSlug: string; reportId: string }>({
      query: ({ teamSlug, reportId }) => `/teams/${encodeURIComponent(teamSlug)}/shared-reports/${encodeURIComponent(reportId)}`,
      invalidatesTags: _ => allTagTypes,
      extraOptions: {
        isMutationOverride: false
      }
    }),

    deleteSharedReport: builder.mutation<void, { reportId: string }>({
      query: ({ reportId }) => ({
        url: `/teams/active/shared-reports/${encodeURIComponent(reportId)}`,
        method: "DELETE"
      }),
      invalidatesTags: ["SharedReports", "SharingOverview"]
    }),

    // Sharing Overview
    listSharingOverview: builder.query<SharingOverviewResponse, void>({
      query: () => `/teams/active/sharing-overview`,
      providesTags: ["SharingOverview"]
    }),

    deletePermalink: builder.mutation<void, { slug: string }>({
      query: ({ slug }) => ({
        url: `/teams/active/permalinks/${encodeURIComponent(slug)}`,
        method: "DELETE"
      }),
      invalidatesTags: ["SharingOverview", "SharedReports", "Dashboards"]
    }),

    updateSharedReportAccessControlList: builder.mutation<SharedReport, { reportId: string } & UpdateViewAccessControlListRequest>({
      query: postQueryDynamicUrl(
        ({ reportId }) => `/teams/active/shared-reports/${encodeURIComponent(reportId)}/access-control-list`,
        ({ accessControlList }) => ({ accessControlList }),
        "PUT"
      ),
      invalidatesTags: ["SharingOverview", "SharedReports"]
    }),

    // Data Pipeline Configuration Endpoints
    getDataPipelineConfiguration: builder.query<DataPipelineConfiguration, { teamId: string, id: string }>({
      query: ({ teamId: _teamId, id }) => `/teams/active/data-pipeline-configurations/${encodeURIComponent(id)}`,
      providesTags: (result, error, { id }) => [{ type: "DataPipelineConfiguration", id }]
    }),

    enterDraftMode: builder.mutation<void, void>({
      query: postNoBodyQuery("/teams/active/data-pipeline-configurations/start-draft", "PUT"),
      invalidatesTags: [
        "AgentData",
        "DashboardModel",
        "MappingConfiguration",
        "Dashboards",
        "DataPipelineConfiguration",
        "ActiveTeam",
        "ContextFiles"
      ],
      async onQueryStarted(newState, { dispatch, queryFulfilled }) {
        await queryFulfilled;

        dispatch(
          api.util.updateQueryData("getTeam", undefined, (draft) => {
            draft.firstRunExperienceState = { ...draft.firstRunExperienceState, dataPipelineConfigurationId: "draft" };
          })
        );
        dispatch(
          api.util.updateQueryData("getCoreApplicationContext", undefined, (draft) => {
            draft.activeTeam = { ...draft.activeTeam, firstRunExperienceState: { ...draft.activeTeam.firstRunExperienceState, dataPipelineConfigurationId: "draft" } };
            draft.viewingDataPipelineConfigurationId = "draft";
          })
        );
      }
    }),

    publishDraft: builder.mutation<void, void>({
      query: postNoBodyQuery("/teams/active/data-pipeline-configurations/publish", "PUT"),
      invalidatesTags: [
        "AgentData",
        "DashboardModel",
        "MappingConfiguration",
        "Dashboards",
        "DataPipelineConfiguration",
        "ActiveTeam",
        "DataPipelineHistory",
        "ContextFiles"
      ],
      async onQueryStarted(newState, { dispatch, queryFulfilled }) {
        await queryFulfilled;

        dispatch(
          api.util.updateQueryData("getTeam", undefined, (draft) => {
            draft.firstRunExperienceState = { ...draft.firstRunExperienceState, dataPipelineConfigurationId: null };
          })
        );
        dispatch(
          api.util.updateQueryData("getCoreApplicationContext", undefined, (draft) => {
            draft.activeTeam = { ...draft.activeTeam, firstRunExperienceState: { ...draft.activeTeam.firstRunExperienceState, dataPipelineConfigurationId: null } };
            draft.viewingDataPipelineConfigurationId = null;
          })
        );
      }
    }),

    discardDraft: builder.mutation<void, void>({
      query: postNoBodyQuery("/teams/active/data-pipeline-configurations/discard", "PUT"),
      invalidatesTags: [
        "AgentData",
        "DashboardModel",
        "MappingConfiguration",
        "Dashboards",
        "DataPipelineConfiguration",
        "ActiveTeam",
        "ContextFiles"
      ],
      async onQueryStarted(newState, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          api.util.updateQueryData("getTeam", undefined, (draft) => {
            draft.firstRunExperienceState = { ...draft.firstRunExperienceState, dataPipelineConfigurationId: null };
          })
        );
        const patchResult2 = dispatch(
          api.util.updateQueryData("getCoreApplicationContext", undefined, (draft) => {
            draft.activeTeam = { ...draft.activeTeam, firstRunExperienceState: { ...draft.activeTeam.firstRunExperienceState, dataPipelineConfigurationId: null } };
            draft.viewingDataPipelineConfigurationId = null;
          })
        );

        try {
          await queryFulfilled;
        }
        catch {
          patchResult.undo();
          patchResult2.undo();
        }
      }
    }),

    // Data Pipeline History
    getDataPipelineHistory: builder.query<DataPipelineHistoryEntry[], void>({
      query: () => `/teams/active/data-pipeline-configurations?view=history`,
      providesTags: ["DataPipelineHistory"]
    }),

    restoreDataPipelineConfiguration: builder.mutation<void, { historyConfigurationId: string }>({
      query: postQuery(
        "/teams/active/data-pipeline-configurations/restore",
        request => request,
        "PUT"
      ),
      invalidatesTags: [
        "AgentData",
        "DashboardModel",
        "MappingConfiguration",
        "Dashboards",
        "DataPipelineConfiguration",
        "ActiveTeam",
        "DataPipelineHistory",
        "ContextFiles"
      ],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        await queryFulfilled;

        dispatch(
          api.util.updateQueryData("getTeam", undefined, (draft) => {
            draft.firstRunExperienceState = { ...draft.firstRunExperienceState, dataPipelineConfigurationId: null };
          })
        );
        dispatch(
          api.util.updateQueryData("getCoreApplicationContext", undefined, (draft) => {
            draft.activeTeam = { ...draft.activeTeam, firstRunExperienceState: { ...draft.activeTeam.firstRunExperienceState, dataPipelineConfigurationId: null } };
            draft.viewingDataPipelineConfigurationId = null;
          })
        );
      }
    }),

    // Customer Implementation Files (Context Files) — scoped per (team, configuration).
    // The list and per-file caches use ContextFiles as the tag type; per-file content
    // also tags itself with `${configurationId}/${path}` so a single-file write does
    // not flush every other open file from the cache. The four draft-transition
    // mutations (enterDraft, publishDraft, discardDraft, restore) include "ContextFiles"
    // in their invalidation lists so the UI re-fetches after the partition swap.
    listContextFiles: builder.query<ContextFileListResponse, { configurationId: string }>({
      query: ({ configurationId }) =>
        `/teams/active/data-pipeline-configurations/${encodeURIComponent(configurationId)}/customer-implementation-files`,
      providesTags: (_result, _error, { configurationId }) => [
        { type: "ContextFiles", id: `list:${configurationId}` }
      ]
    }),
    getContextFileContent: builder.query<ContextFileContentResponse, { configurationId: string; path: string }>({
      query: ({ configurationId, path }) =>
        `/teams/active/data-pipeline-configurations/${encodeURIComponent(configurationId)}/customer-implementation-files/content?path=${encodeURIComponent(path)}`,
      providesTags: (_result, _error, { configurationId, path }) => [
        { type: "ContextFiles", id: `${configurationId}/${path}` }
      ]
    }),
    updateContextFileContent: builder.mutation<void, { configurationId: string; path: string; content: string }>({
      query: ({ configurationId, path, content }) => ({
        url: `/teams/active/data-pipeline-configurations/${encodeURIComponent(configurationId)}/customer-implementation-files/content?path=${encodeURIComponent(path)}`,
        method: "PUT",
        body: { content },
        headers: { "Content-Type": "application/json" }
      }),
      // Optimistically reflect the saved content in the per-file cache so the editor shows the
      // just-saved text the instant it blurs / Done is clicked, instead of the stale value until the
      // (git-commit-latency) save round-trips. Undo on failure. The content tag is still invalidated
      // below so the canonical server value (e.g. any normalization) is refetched after success.
      async onQueryStarted({ configurationId, path, content }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          api.util.updateQueryData("getContextFileContent", { configurationId, path }, (draft) => {
            draft.content = content;
          })
        );
        try {
          await queryFulfilled;
        }
        catch {
          patchResult.undo();
        }
      },
      invalidatesTags: (_result, _error, { configurationId, path }) => [
        { type: "ContextFiles", id: `${configurationId}/${path}` }
      ]
    }),
    createContextFile: builder.mutation<void, { configurationId: string; path: string; content: string }>({
      query: ({ configurationId, path, content }) => ({
        url: `/teams/active/data-pipeline-configurations/${encodeURIComponent(configurationId)}/customer-implementation-files/content?path=${encodeURIComponent(path)}`,
        method: "POST",
        body: { content },
        headers: { "Content-Type": "application/json" }
      }),
      invalidatesTags: (_result, _error, { configurationId }) => [
        { type: "ContextFiles", id: `list:${configurationId}` }
      ]
    }),
    deleteContextFile: builder.mutation<void, { configurationId: string; path: string }>({
      query: ({ configurationId, path }) => ({
        url: `/teams/active/data-pipeline-configurations/${encodeURIComponent(configurationId)}/customer-implementation-files/content?path=${encodeURIComponent(path)}`,
        method: "DELETE"
      }),
      invalidatesTags: (_result, _error, { configurationId, path }) => [
        { type: "ContextFiles", id: `list:${configurationId}` },
        { type: "ContextFiles", id: `${configurationId}/${path}` }
      ]
    }),
    /**
     * Uploads a binary context file. The body is the raw file bytes (no JSON envelope) so the
     * backend reads them straight off the request stream — that's why the Content-Type is set
     * to application/octet-stream rather than the usual application/json.
     */
    uploadContextFile: builder.mutation<void, { configurationId: string; path: string; file: Blob }>({
      query: ({ configurationId, path, file }) => ({
        url: `/teams/active/data-pipeline-configurations/${encodeURIComponent(configurationId)}/customer-implementation-files/upload?path=${encodeURIComponent(path)}`,
        method: "POST",
        body: file,
        headers: { "Content-Type": "application/octet-stream" }
      }),
      invalidatesTags: (_result, _error, { configurationId }) => [
        { type: "ContextFiles", id: `list:${configurationId}` }
      ]
    }),
    /**
     * Replaces (or creates) a context file in place, overwriting any existing file at the path.
     * Mirrors uploadContextFile but uses PUT (create-or-overwrite) rather than POST (create-only),
     * and also invalidates the per-file content cache so an open viewer/editor re-fetches the new bytes.
     */
    replaceContextFile: builder.mutation<void, { configurationId: string; path: string; file: Blob }>({
      query: ({ configurationId, path, file }) => ({
        url: `/teams/active/data-pipeline-configurations/${encodeURIComponent(configurationId)}/customer-implementation-files/upload?path=${encodeURIComponent(path)}`,
        method: "PUT",
        body: file,
        headers: { "Content-Type": "application/octet-stream" }
      }),
      invalidatesTags: (_result, _error, { configurationId, path }) => [
        { type: "ContextFiles", id: `list:${configurationId}` },
        { type: "ContextFiles", id: `${configurationId}/${path}` }
      ]
    }),

    // Gold Pipeline Trigger
    triggerGoldPipeline: builder.mutation<void, { dataPipelineConfigurationId: string; isFullRun: boolean }>({
      query: postNoBodyQueryDynamicUrl(
        ({ dataPipelineConfigurationId, isFullRun }) => `/teams/active/gold-pipeline/${encodeURIComponent(dataPipelineConfigurationId)}/trigger${isFullRun ? "?runAll=true" : ""}`
      ),
      invalidatesTags: ["DashboardRecords", { type: "GoldPipelineStatus" }],
      // Optimistically flip the status to Queued so the UI reflects the trigger instantly,
      // rather than waiting on the POST plus the invalidation-driven refetch. The real server
      // state replaces it when the status query refetches.
      async onQueryStarted({ dataPipelineConfigurationId, isFullRun }, { dispatch, queryFulfilled }) {
        const nowIso = new Date().toISOString();
        const patchResult = dispatch(
          api.util.updateQueryData("getGoldPipelineStatus", { dataPipelineConfigurationId }, draft => {
            if (draft == null) return;
            draft.status = ImportStatus.Queued;
            draft.stage = GoldPipelineStage.Queued;
            draft.startedAt = nowIso;
            draft.completedAt = null;
            draft.errorMessage = null;
            draft.specsExecuted = 0;
            draft.recordsTransformed = 0;
            draft.specTimings = {};
            draft.isFullRun = isFullRun;
            draft.executedSpecs = [];
            draft.copiedSpecs = [];
            draft.dirtySources = [];
          })
        );

        try {
          await queryFulfilled;
        }
        catch {
          patchResult.undo();
        }
      }
    }),
    // Gold Pipeline Cancel
    cancelGoldPipeline: builder.mutation<void, { dataPipelineConfigurationId: string }>({
      query: postNoBodyQueryDynamicUrl(
        ({ dataPipelineConfigurationId }) => `/teams/active/gold-pipeline/${encodeURIComponent(dataPipelineConfigurationId)}/cancel`
      ),
      invalidatesTags: ["DashboardRecords", { type: "GoldPipelineStatus" }]
    }),
    // Data Export Endpoints
    startDataExport: builder.mutation<QueueAgentOperationResponse, StartDataExportRequest>({
      query: postQuery(
        "/teams/active/data-exports",
        request => request
      )
    }),
    getDataExportStatus: builder.query<AgentOperationResult<DataExportOperationInput, unknown>, { applicationContext: string }>({
      query: ({ applicationContext }) => `/teams/active/data-exports/${encodeURIComponent(applicationContext)}`,
      keepUnusedDataFor: 0
    }),
    // Validation
    getValidationSuite: builder.query<ValidationSuiteDetail, void>({
      query: () => "/teams/active/validation/suite",
      providesTags: ["ValidationSuite", "ValidationRun"]
    }),
    triggerValidationRun: builder.mutation<ValidationRunSummary, void>({
      query: postNoBodyQuery("/teams/active/validation/runs"),
      invalidatesTags: ["ValidationSuite", "ValidationRun"]
    }),
    // SignalR
    signalRNegotiate: builder.mutation<{ url: string; accessToken: string }, void>({
      query: postNoBodyQuery("/signalr/negotiate")
    }),
  }),
});

export const {
  useSignOutMutation,
  useGetMfeTokenMutation,
  useCreateMfeSessionMutation,
  useAuthenticateTestUserMutation,
  useAuthenticateEmailPasswordUserMutation,
  useLazyValidateInviteQuery,
  useLazyAgentGenerateQuery,
  useGetSelectSourcesAgentDataQuery,
  useUpdateSelectSourcesAgentDataMutation,
  useGetComprehensiveDraftDiffQuery,
  useLazyGetComprehensiveDraftDiffQuery,
  useGetCoreApplicationContextQuery,
  useLazyGetCoreApplicationContextQuery,
  useGetTeamQuery,
  useLazyGetTeamQuery,
  useUpdateGlobalTeamDataMutation,
  useGetTeamSlugQuery,
  useUpdateTeamSlugMutation,
  useValidateTeamSlugQuery,
  useLazyValidateTeamSlugQuery,
  useChangeActiveTeamMutation,
  useCreateNewTeamMutation,
  useInviteTeamMemberMutation,
  useUpdateTeamNameMutation,
  useUpdateAppSettingsMutation,
  useUpdateActivePipelineConfigurationIdMutation,
  useUpdateFirstRunExperienceStateMutation,
  useRemoveTeamInviteMutation,
  useRemoveTeamMemberMutation,
  useUpdateTeamMemberRoleMutation,
  useCreateTeamGroupMutation,
  useUpdateTeamGroupMutation,
  useDeleteTeamGroupMutation,
  useUpdateAccountMutation,
  useCreateSubscriptionCheckoutSessionMutation,
  useUpdateSubscriptionCheckoutSessionMutation,
  useCancelAccountPlanMutation,
  useLazyGetSubscriptionDataQuery,
  useLazyGetDowngradeViolationQuery,
  useChangeAccountPlanMutation,
  useChangeAccountPlanAddOnMutation,
  useCreateMcpServerTokenQuery,
  useLazyCreateMcpServerTokenQuery,
  useOauth2ConsentMutation,
  useGetOAuth2ClientQuery,
  useGetContextItemQuery,
  useCreateContextItemMutation,
  useUpdateContextItemMutation,
  useDeleteContextItemMutation,
  useConnectGitHubAppMutation,
  useCompleteConnectGitHubAppMutation,
  useInitiateConnectGitHubAppVerifiedMutation,
  useCompleteConnectGitHubAppVerifiedMutation,
  useInitiateConnectExistingMutation,
  useCompleteConnectExistingMutation,
  useConnectDiscoveredInstallationMutation,
  useDisconnectGitHubAppMutation,
  useGetGitHubStatusQuery,
  useGetRepositoryBranchesQuery,
  useSetGitHubRepositoryMutation,
  useSetGitHubBranchMutation,
  useProvisionManagedRepoMutation,
  useDisconnectManagedOrgMutation,
  useLinkGitHubAccountMutation,
  useCompleteLinkGitHubAccountMutation,
  useUnlinkGitHubAccountMutation,
  useConnectHubSpotMutation,
  useCompleteConnectHubSpotMutation,
  useDisconnectHubSpotMutation,
  useGetHubSpotStatusQuery,
  useConnectStripeMutation,
  useCompleteConnectStripeMutation,
  useDisconnectStripeMutation,
  useGetStripeStatusQuery,
  useGetSalesforceStatusQuery,
  useConfigureSalesforceMutation,
  useDeleteSalesforceCredentialsMutation,
  useConnectSalesforceMutation,
  useCompleteConnectSalesforceMutation,
  useDisconnectSalesforceMutation,
  useConfigureNetSuiteMutation,
  useUploadNetSuitePrivateKeyMutation,
  useConnectNetSuiteMutation,
  useCompleteConnectNetSuiteMutation,
  useDisconnectNetSuiteMutation,
  useGetNetSuiteStatusQuery,
  useConnectQuickBooksMutation,
  useCompleteConnectQuickBooksMutation,
  useDisconnectQuickBooksMutation,
  useGetQuickBooksStatusQuery,
  useConfigureMaxioMutation,
  useDisconnectMaxioMutation,
  useGetMaxioStatusQuery,
  useConfigureRecurlyMutation,
  useDisconnectRecurlyMutation,
  useGetRecurlyStatusQuery,
  useConnectMetaMutation,
  useCompleteConnectMetaMutation,
  useDisconnectMetaMutation,
  useGetMetaStatusQuery,
  useGetMetaAdAccountsQuery,
  useUpdateMetaAdAccountSelectionMutation,
  useConfigureMaxioAdvancedBillingMutation,
  useDisconnectMaxioAdvancedBillingMutation,
  useGetMaxioAdvancedBillingStatusQuery,
  useConfigureStayAiMutation,
  useDisconnectStayAiMutation,
  useGetStayAiStatusQuery,
  useConnectShopifyMutation,
  useCompleteConnectShopifyMutation,
  useDisconnectShopifyMutation,
  useGetShopifyStatusQuery,
  useConnectGoogleAdsMutation,
  useCompleteConnectGoogleAdsMutation,
  useDisconnectGoogleAdsMutation,
  useGetGoogleAdsStatusQuery,
  useConnectGoogleDriveMutation,
  useCompleteConnectGoogleDriveMutation,
  useDisconnectGoogleDriveMutation,
  useGetGoogleDriveStatusQuery,
  useLazyGetGoogleDrivePickerConfigQuery,
  useGetGoogleDriveSelectedFilesQuery,
  useSetGoogleDriveSelectedFilesMutation,
  useSetGoogleDriveSelectedFolderMutation,
  useConnectIntakeAmazonDspMutation,
  useCompleteConnectIntakeAmazonDspMutation,
  useDisconnectIntakeAmazonDspMutation,
  useGetIntakeAmazonDspStatusQuery,
  useLazyGetIntakeAmazonDspPickerConfigQuery,
  useSetIntakeAmazonDspFolderMutation,
  useConnectTikTokAdsMutation,
  useCompleteConnectTikTokAdsMutation,
  useDisconnectTikTokAdsMutation,
  useGetTikTokAdsStatusQuery,
  useConnectTikTokSellerMutation,
  useCompleteConnectTikTokSellerMutation,
  useDisconnectTikTokSellerMutation,
  useGetTikTokSellerStatusQuery,
  useConnectAmazonAdsMutation,
  useCompleteConnectAmazonAdsMutation,
  useDisconnectAmazonAdsMutation,
  useGetAmazonAdsStatusQuery,
  useConnectAmazonDspMutation,
  useCompleteConnectAmazonDspMutation,
  useDisconnectAmazonDspMutation,
  useGetAmazonDspStatusQuery,
  useConnectAmazonSellerMutation,
  useCompleteConnectAmazonSellerMutation,
  useDisconnectAmazonSellerMutation,
  useGetAmazonSellerStatusQuery,
  useGetConnectorSchemasQuery,
  useGetConnectorSyncStatusQuery,
  useGetConnectorSyncHistoryQuery,
  useGetConnectorRecordsQuery,
  useGetAllConnectorSyncStatusesQuery,
  useGetBronzeSchemaContextQuery,
  useGetGoldPipelineStatusQuery,
  useTriggerConnectorSyncMutation,
  useGetConnectorSettingsQuery,
  useUpdateConnectorSettingsMutation,
  useUpdateConnectorSyncScheduleMutation,
  useStartConnectorSyncScheduleMutation,
  useStopConnectorSyncScheduleMutation,
  useGetDashboardModelQuery,
  useGetMappingConfigurationQuery,
  useGetDashboardRecordsQuery,
  useGetAgentOperationStatusQuery,
  useLazyGetAgentOperationStatusQuery,
  useGetDatabricksDataQuery,
  useExecuteSqlQueryMutation,
  useLazyGetDashboardQuery,
  useGetWidgetDataQuery,
  useGetDrilldownDataQuery,
  useGetKpiDataQuery,
  useGetOpsAppObjectDefinitionsQuery,
  useListOpsAppObjectsQuery,
  useLazyListOpsAppObjectsQuery,
  useGetOpsAppObjectQuery,
  useLazyGetOpsAppObjectQuery,
  useUpsertOpsAppObjectMutation,
  useDeleteOpsAppObjectMutation,
  useGetDashboardViewAccessControlListQuery,
  useUpdateDashboardViewAccessControlListMutation,
  useCreateViewPermalinkMutation,
  useValidatePermalinkSlugQuery,
  useLazyValidatePermalinkSlugQuery,
  useResolvePermalinkMutation,
  useCreateSharedReportMutation,
  useListSharedReportsQuery,
  useGetSharedReportMutation,
  useDeleteSharedReportMutation,
  useListSharingOverviewQuery,
  useDeletePermalinkMutation,
  useUpdateSharedReportAccessControlListMutation,
  useGetDashboardQuery,
  useGetDataPipelineConfigurationQuery,
  useEnterDraftModeMutation,
  usePublishDraftMutation,
  useDiscardDraftMutation,
  useGetDataPipelineHistoryQuery,
  useRestoreDataPipelineConfigurationMutation,
  useListContextFilesQuery,
  useGetContextFileContentQuery,
  useUpdateContextFileContentMutation,
  useCreateContextFileMutation,
  useDeleteContextFileMutation,
  useUploadContextFileMutation,
  useReplaceContextFileMutation,
  useTriggerGoldPipelineMutation,
  useCancelGoldPipelineMutation,
  useGetCustomStatusQuery,
  useGetCustomConnectionsQuery,
  useGetCustomConnectionQuery,
  useStartCustomConnectionUploadMutation,
  useCompleteCustomConnectionUploadMutation,
  useUpdateCustomConnectionMutation,
  useDeleteCustomConnectionMutation,
  useStartDataExportMutation,
  useLazyGetDataExportStatusQuery,
  useGetDataExportStatusQuery,
  useGetValidationSuiteQuery,
  useTriggerValidationRunMutation,
  useSignalRNegotiateMutation
} = api;
